import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import DocumentPickerDialog from '@/components/dms/DocumentPickerDialog';
import TaskAttachmentsDialog from '@/components/dms/TaskAttachmentsDialog';
import { useProject } from '@/contexts/ProjectContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Plus, Trash2, ChevronRight, ChevronDown, Indent, Outdent, 
  Link2, Unlink, Copy, Scissors, Clipboard, Calculator, RefreshCw,
  FileSpreadsheet, Calendar, Users, TrendingUp, LineChart, AlertTriangle,
  Flag, Settings, MoreHorizontal, Activity, HelpCircle, Clock, GripVertical,
  Save, Percent, Check, X, Edit, Pencil, ChevronUp, Sparkles, BarChart3, Zap, Loader2, Paperclip, FileText, Trash
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, AreaChart, Area, 
  BarChart, Bar, ComposedChart, ScatterChart, Scatter, Cell, ReferenceLine
} from 'recharts';

// AG Grid Component
import AGGridWBS from '@/components/scheduling/AGGridWBS';
import RecalcDiffPanel from '@/components/scheduling/RecalcDiffPanel';
import RecalcPolicyModal from '@/components/scheduling/RecalcPolicyModal';
import CascadePanel from '@/components/scheduling/CascadePanel';
import ScheduleVersionsDrawer from '@/components/scheduling/ScheduleVersionsDrawer';
import '@/components/scheduling/ag-grid-theme.css';

// AI Schedule Generator Panel
import AISchedulePanel from '@/components/scheduling/AISchedulePanel';
import RollUpDashboard from '@/components/rollup/RollUpDashboard';
import ScheduleInsights from '@/components/scheduling/ScheduleInsights';
import SchedulePredictOps from '@/components/scheduling/SchedulePredictOps';
import MacroDetailPanel from '@/components/rollup/MacroDetailPanel';
import MilestoneBar from '@/components/scheduling/MilestoneBar';
import GanttView from '@/components/scheduling/GanttView';
import ScheduleSCurveChart from '@/components/charts/ScheduleSCurveChart';

// ============================================
// HELPER FUNCTIONS
// ============================================

// Safely extract error message from API error response
// Handles both string detail and Pydantic validation error arrays
const getErrorMessage = (err, fallback = 'An error occurred') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation error format: [{type, loc, msg, input, url}]
    return detail.map(e => e.msg || e.message || String(e)).join(', ') || fallback;
  }
  return String(detail);
};

const formatDuration = (duration, unit = 'days') => {
  if (!duration && duration !== 0) return '';
  
  // Backend always stores duration in hours (days × 8)
  // Always display in days by dividing by 8
  // Exception: if duration_unit is explicitly 'days', it's already in days
  let days;
  if (unit === 'days') {
    // Already in days format
    days = typeof duration === 'number' ? duration : parseFloat(duration);
  } else {
    // Stored in hours - convert to days
    days = typeof duration === 'number' ? duration / 8 : parseFloat(duration) / 8;
  }
  
  // Round to 1 decimal place and format
  const rounded = Math.round(days * 10) / 10;
  // Show as integer if it's a whole number
  return `${rounded % 1 === 0 ? Math.round(rounded) : rounded}d`;
};

const parseDuration = (str, unit = 'days') => {
  if (!str) return 0;
  const dayMatch = str.match(/(\d+(?:\.\d+)?)\s*d/i);
  const hourMatch = str.match(/(\d+(?:\.\d+)?)\s*h/i);
  
  if (dayMatch) {
    // Input is in days - convert to hours for storage (days × 8)
    return parseFloat(dayMatch[1]) * 8;
  }
  if (hourMatch) {
    // Input is in hours - store directly
    return parseFloat(hourMatch[1]);
  }
  // Plain number - assume it's in days, convert to hours
  const days = parseFloat(str) || 0;
  return days * 8;
};

// Check if a task is editable for a specific field
const isFieldEditable = (task, fieldId) => {
  // Summary tasks: only name is editable
  if (task.is_summary) {
    return fieldId === 'name';
  }
  
  // Milestone tasks: duration is not editable
  if (task.is_milestone && fieldId === 'duration') {
    return false;
  }
  
  // Dependencies not allowed on summary tasks
  if (task.is_summary && fieldId === 'predecessors') {
    return false;
  }
  
  return true;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  } catch { return ''; }
};

const formatPredecessors = (deps, tasks) => {
  if (!deps || deps.length === 0) return '';
  return deps.map(dep => {
    const pred = tasks.find(t => t.id === dep.predecessor_id);
    if (!pred) return '';
    // Type abbreviation (FS is default, so don't show it)
    const typeMap = {
      'finish_to_start': '',
      'start_to_start': 'SS',
      'finish_to_finish': 'FF',
      'start_to_finish': 'SF'
    };
    const type = typeMap[dep.type] || '';
    // Lag with unit
    let lag = '';
    if (dep.lag && dep.lag !== 0) {
      const unit = dep.lag_type === 'days' ? 'd' : 'h';
      lag = dep.lag > 0 ? `+${dep.lag}${unit}` : `${dep.lag}${unit}`;
    }
    return `${pred.uid}${type}${lag}`;
  }).filter(Boolean).join(', ');
};

// ============================================
// MAIN COMPONENT
// ============================================
export default function SchedulingDashboardEnhanced() {
  const { scheduleProjectId } = useParams();
  const navigate = useNavigate();
  const { projectId: parentProjectId } = useProject();
  const { can: canPerm } = usePermissions();
  const canSchedCreate = canPerm('MACRO_SCHEDULE', 'create');
  const canSchedEdit = canPerm('MACRO_SCHEDULE', 'edit');
  const canSchedDelete = canPerm('MACRO_SCHEDULE', 'delete');
  const canSchedImport = canPerm('MACRO_SCHEDULE', 'import');
  const canSchedExport = canPerm('MACRO_SCHEDULE', 'export');
  const canSchedConfigure = canPerm('MACRO_SCHEDULE', 'configure');
  
  // Data State
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [resources, setResources] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [baselines, setBaselines] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [sCurveData, setSCurveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [lastRecalculation, setLastRecalculation] = useState(null);
  const [recalcDiffPanel, setRecalcDiffPanel] = useState({ open: false, summary: null, diffs: [] });
  // 4-Policy Recalculate Modal
  const [policyModal, setPolicyModal] = useState({
    open: false,
    loading: false,
    applying: false,
    preview: null,
    changedTaskId: null,
  });
  // Predecessor Cascade + Undo state (set after a policy is applied)
  const [cascade, setCascade] = useState({ open: false, loading: false, payload: null, snapshotId: null });
  const [versionsDrawerOpen, setVersionsDrawerOpen] = useState(false);
  // Cognara Schedule Impact Narrative + impacted task list
  const [impactDialogOpen, setImpactDialogOpen] = useState(false);
  const [impactReport, setImpactReport] = useState(() => {
    // Rehydrate the last Cognara CPM analysis for this schedule so users
    // can re-open "View Last Analysis" even after a refresh or tab switch.
    try {
      // scheduleProjectId isn't in scope yet at initializer time, but
      // useEffect below will handle the id-scoped restore too.
      return null;
    } catch { return null; }
  });

  // ── Rehydrate the CPM analysis from localStorage once the schedule id
  //    is known, so "View Last Analysis" is available after a refresh.
  useEffect(() => {
    if (!scheduleProjectId || impactReport) return;
    try {
      const raw = localStorage.getItem(`cpm-analysis:${scheduleProjectId}`);
      if (!raw) return;
      const stored = JSON.parse(raw);
      if (stored?.narrative) setImpactReport(stored);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleProjectId]);
  const [riskPickerOpen, setRiskPickerOpen] = useState(false);
  const [selectedRiskIds, setSelectedRiskIds] = useState(new Set());
  const [creatingRisks, setCreatingRisks] = useState(false);
  const [riskDetails, setRiskDetails] = useState({}); // taskId -> {owner, category, description, mitigation, probability, impact, response_strategy}
  const [detailEditingId, setDetailEditingId] = useState(null);
  const defaultRiskDetail = (t) => ({
    owner: 'Schedule Manager',
    domainCategory: t.is_critical ? 'PROGRAMME' : 'PROGRAMME',
    description: t.reason || `Schedule slip on ${t.name}`,
    mitigation: '',
    probabilityScore: t.is_critical ? 4 : 3,
    impactScore: t.is_critical ? 5 : 4,
    responseStrategy: 'MITIGATE',
    priority: t.is_critical ? 'HIGH' : Math.abs(t.ds_finish_days || 0) >= 5 ? 'MEDIUM' : 'LOW',
  });
  // CPM auto-mode — OFF by default so imported MPP/XER dates stay exactly
  // as in the source file. The user must explicitly click "Recalculate"
  // to run CPM, or flip this toggle on to get the legacy auto-on behavior.
  const [cpmAutoMode, setCpmAutoMode] = useState(() => {
    try { return localStorage.getItem('cpmAutoMode') === 'true'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('cpmAutoMode', String(cpmAutoMode)); }
    catch { /* ignore */ }
  }, [cpmAutoMode]);
  const [projectDurationUnit, setProjectDurationUnit] = useState('days'); // Project-level duration unit - always days
  
  // UI State
  const [activeTab, setActiveTab] = useState('wbs');
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [focusedRow, setFocusedRow] = useState(0);
  const [focusedCol, setFocusedCol] = useState(0);
  const [clipboard, setClipboard] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [attachDocsForTask, setAttachDocsForTask] = useState(null);
  const [taskAttachmentCounts, setTaskAttachmentCounts] = useState({});
  const [bulkAttachTaskIds, setBulkAttachTaskIds] = useState(null);   // array of task ids for bulk attach
  const [showBulkAttachPicker, setShowBulkAttachPicker] = useState(false);
  const [bulkAttaching, setBulkAttaching] = useState(false);
  const [showBaselineDialog, setShowBaselineDialog] = useState(false);
  const [newBaselineName, setNewBaselineName] = useState('');
  const [baselineComparison, setBaselineComparison] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  
  // Dialog states
  const [showPredecessorDialog, setShowPredecessorDialog] = useState(false);
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  const [showCalendarDialog, setShowCalendarDialog] = useState(false);
  const [showConstraintDialog, setShowConstraintDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  // AI Schedule Generator state
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [showDeleteScheduleDialog, setShowDeleteScheduleDialog] = useState(false);
  const [isDeletingSchedule, setIsDeletingSchedule] = useState(false);
  
  const gridRef = useRef(null);
  const inputRef = useRef(null);

  // Default column definitions
  const defaultColumns = [
    { id: 'wbs_code', label: 'WBS', width: 70, editable: false },
    { id: 'name', label: 'Task Name', width: 200, editable: true },
    { id: 'is_milestone', label: 'MS', width: 40, editable: true, type: 'milestone' },
    { id: 'duration', label: 'Duration', width: 85, editable: true, type: 'duration' },
    { id: 'scheduled_start', label: 'Start', width: 95, editable: true, type: 'date' },
    { id: 'scheduled_finish', label: 'Finish', width: 95, editable: false, type: 'date' },
    { id: 'baseline_start', label: 'BL Start', width: 95, editable: false, type: 'date' },
    { id: 'baseline_finish', label: 'BL Finish', width: 95, editable: false, type: 'date' },
    { id: 'actual_start', label: 'Act Start', width: 95, editable: true, type: 'date' },
    { id: 'actual_finish', label: 'Act Finish', width: 95, editable: true, type: 'date' },
    { id: 'predecessors', label: 'Predecessors', width: 100, editable: true, type: 'predecessor' },
    { id: 'calendar_id', label: 'Calendar', width: 100, editable: true, type: 'calendar' },
    { id: 'resources', label: 'Resources', width: 110, editable: true, type: 'resource' },
    { id: 'constraint_type', label: 'Constraint', width: 90, editable: true, type: 'constraint' },
    { id: 'weightage', label: 'Wgt %', width: 65, editable: true, type: 'percentage' },
    { id: 'percent_complete', label: 'Progress %', width: 85, editable: true, type: 'percentage' }
  ];

  // Custom fields imported from MPP/XER — user toggles which to surface.
  // Selection persists per schedule-project in localStorage.
  const customFieldSchema = project?.custom_field_schema || [];
  const customColsStorageKey = scheduleProjectId ? `customCols:${scheduleProjectId}` : 'customCols:_';
  const [selectedCustomCols, setSelectedCustomCols] = useState(() => {
    try {
      const raw = localStorage.getItem(customColsStorageKey);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(customColsStorageKey, JSON.stringify(selectedCustomCols)); } catch {/* ignore */}
  }, [customColsStorageKey, selectedCustomCols]);
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const columns = React.useMemo(() => {
    const extra = selectedCustomCols
      .map((key) => customFieldSchema.find((s) => s.key === key))
      .filter(Boolean)
      .map((s) => ({
        id: `custom_${s.key}`,
        label: s.label,
        width: s.type === 'date' ? 105 : (s.type === 'number' || s.type === 'cost') ? 90 : 130,
        editable: false,
        type: s.type === 'date' ? 'date' : s.type,
        _customKey: s.key,
      }));
    return [...defaultColumns, ...extra];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomCols, customFieldSchema]);


  // Fetch project data — resilient to transient 502/504 errors from
  // long-running edges (S-Curve / baselines / calendars can race).
  const fetchProject = useCallback(async () => {
    if (!scheduleProjectId) return;
    try {
      setLoading(true);
      const projectRes = await api.get(`/scheduling/projects/${scheduleProjectId}`);
      // Settle in parallel; any peripheral 502 shouldn't fail the page.
      const [sCurveRes, baselinesRes, calendarsRes] = await Promise.allSettled([
        api.get(`/scheduling/projects/${scheduleProjectId}/s-curve`),
        api.get(`/scheduling/projects/${scheduleProjectId}/baselines`),
        api.get(`/scheduling/projects/${scheduleProjectId}/calendars`),
      ]);

      setProject(projectRes.data);
      setTasks(projectRes.data.tasks || []);
      setResources(projectRes.data.resources || []);
      setCalendars(calendarsRes.status === 'fulfilled' ? (calendarsRes.value.data || []) : []);
      setMetrics(projectRes.data.metrics || {});
      setSCurveData(sCurveRes.status === 'fulfilled' ? sCurveRes.value.data : null);
      setBaselines(baselinesRes.status === 'fulfilled' ? (baselinesRes.value.data || []) : []);

      // Attachment counts per task (best-effort; failure is silent)
      api.get(`/scheduling/projects/${scheduleProjectId}/attachments/summary`)
        .then(r => setTaskAttachmentCounts(r.data?.by_task || {}))
        .catch(() => setTaskAttachmentCounts({}));

      // Expand all summary tasks by default
      const summaryIds = new Set((projectRes.data.tasks || []).filter(t => t.is_summary).map(t => t.id));
      setExpandedTasks(summaryIds);
    } catch (err) {
      console.error('Failed to load project:', err);
      // Only surface the toast for genuine project-load failures (the core
      // /projects/<id> call). Peripheral 502s are silenced via allSettled
      // above so a single overloaded edge no longer blocks the whole page.
      toast.error(err?.response?.data?.detail || 'Failed to load project — retrying may help');
    } finally {
      setLoading(false);
    }
  }, [scheduleProjectId]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  // Deep-link support: ?taskId=MS-MAT-... → scroll to task row after load
  useEffect(() => {
    if (!tasks.length) return;
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get('taskId');
    if (!targetId) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-task-row="${targetId}"]`) ||
                 document.getElementById(`task-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const orig = el.style.background;
        el.style.background = 'rgba(242,107,31,0.25)';
        el.style.transition = 'background 0.4s';
        setTimeout(() => { el.style.background = orig; }, 4000);
      } else {
        toast.info(`Task ${targetId} not visible — expand summary rows to see the milestone.`);
      }
    }, 600);
  }, [tasks]);

  // Get visible tasks (respecting hierarchy collapse/expand)
  const getVisibleTasks = useCallback(() => {
    const sorted = [...tasks].sort((a, b) => {
      if (a.wbs_code < b.wbs_code) return -1;
      if (a.wbs_code > b.wbs_code) return 1;
      return a.position - b.position;
    });
    
    const visible = [];
    for (const task of sorted) {
      let isVisible = true;
      let parentId = task.parent_id;
      while (parentId) {
        if (!expandedTasks.has(parentId)) { isVisible = false; break; }
        const parent = tasks.find(t => t.id === parentId);
        parentId = parent?.parent_id;
      }
      if (isVisible) visible.push(task);
    }
    return visible;
  }, [tasks, expandedTasks]);

  const visibleTasks = getVisibleTasks();

  // ============================================
  // TASK OPERATIONS
  // ============================================
  const addTask = async (afterTaskId = null, parentId = null) => {
    // Use the new insert-row API for proper position handling
    if (afterTaskId) {
      // Insert below the selected task using the new API
      await insertRow(afterTaskId, 'below', null, 1);
    } else if (tasks && tasks.length > 0) {
      // No task selected - insert at the end (after last task)
      const lastTask = tasks[tasks.length - 1];
      await insertRow(lastTask.id, 'below', null, 1);
    } else {
      // No tasks exist - use old quick-add as fallback for first task
      try {
        const params = { project_id: scheduleProjectId };
        if (parentId) params.parent_id = parentId;
        
        const res = await api.post('/scheduling/tasks/quick-add', null, { params });
        
        if (res.data) {
          setTasks(prev => [...prev, res.data]);
          toast.success('New task created');
        }
      } catch (err) {
        toast.error('Failed to add task');
      }
    }
  };

  const deleteTask = async (taskId) => {
    try {
      // Optimistic update - remove from UI immediately
      setTasks(prev => prev.filter(t => t.id !== taskId));
      
      await api.delete(`/scheduling/tasks/${taskId}`);
      toast.success('Task deleted');
      
      // Trigger background recalculation
      triggerRecalculation();
    } catch (err) {
      toast.error('Failed to delete task');
      // Revert on error - refetch
      await fetchProject();
    }
  };

  const updateTask = async (taskId, updates, skipRecalc = false) => {
    try {
      // Optimistic update - update local state immediately
      setTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, ...updates, updated_at: new Date().toISOString() } : t
      ));
      
      // Send to backend
      const response = await api.put(`/scheduling/tasks/${taskId}`, updates);
      
      // Handle warnings from backend (actual date auto-capture notifications)
      if (response.data._warnings) {
        response.data._warnings.forEach(w => {
          if (w.type === 'info') {
            toast.success(w.message, { duration: 4000 });
          } else if (w.type === 'warning') {
            toast.warning(w.message, { duration: 5000 });
          } else if (w.type === 'action_required') {
            // Actual date was auto-set but user can/should edit it
            toast.info(w.message, { 
              duration: 6000,
              description: w.allow_past_dates 
                ? 'You can select any past date if the activity started/finished earlier.' 
                : undefined
            });
          } else if (w.type === 'auto_adjusted') {
            // Date was auto-adjusted due to predecessor constraint violation
            toast.warning(w.message, { 
              duration: 8000,
              description: `Date was automatically adjusted to respect dependency constraints.`
            });
          }
        });
        // Remove _warnings before storing in state
        delete response.data._warnings;
      }
      
      // Update with server response (includes calculated fields)
      setTasks(prev => prev.map(t => 
        t.id === taskId ? response.data : t
      ));
      
      // Trigger background recalculation if dependencies, dates, or progress changed
      if (!skipRecalc && (updates.dependencies || updates.scheduled_start || updates.duration || 
          updates.actual_start || updates.actual_finish || updates.percent_complete)) {
        triggerRecalculation();
      }

      // Auto-rollup preview — silently check if any summary parent needs recalculation
      if (!skipRecalc && (updates.scheduled_start || updates.scheduled_finish || updates.duration || updates.percent_complete)) {
        triggerRollupCheck();
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update task'));
      // Revert on error - refetch
      await fetchProject();
    }
  };

  // Debounced background rollup-preview probe. If parents need recomputing,
  // toast a subtle reminder pointing users to Insights → Roll-up.
  const triggerRollupCheck = useCallback(() => {
    if (window.rollupCheckTimer) clearTimeout(window.rollupCheckTimer);
    window.rollupCheckTimer = setTimeout(async () => {
      try {
        const r = await api.get(`/scheduling/schedules/${scheduleProjectId}/rollup/preview`);
        const n = r.data?.changes_needed || 0;
        if (n > 0) {
          toast(`Roll-up needed on ${n} summary task${n === 1 ? '' : 's'}`, {
            duration: 4500,
            description: 'Open Insights → Roll-up to review and apply.',
          });
        }
      } catch (_) { /* silent — this is a background probe */ }
    }, 2500);
  }, [scheduleProjectId]);
  
  // Debounced recalculation trigger — only fires when the user has
  // explicitly opted into CPM auto-mode. Default behaviour preserves
  // imported MPP/XER dates exactly; the explicit "Recalculate" toolbar
  // button is the only path to CPM.
  const triggerRecalculation = async () => {
    if (!cpmAutoMode) return;
    // Debounce - wait 500ms before triggering recalculation
    if (window.recalcTimer) clearTimeout(window.recalcTimer);
    
    window.recalcTimer = setTimeout(async () => {
      try {
        setIsRecalculating(true);
        const response = await api.post(
          `/scheduling/projects/${scheduleProjectId}/schedule/recalculate`,
          {}
        );
        
        // Update tasks with recalculated values
        if (response.data.status === 'Completed') {
          // Fetch updated tasks silently (no loading state)
          const tasksResponse = await api.get(`/scheduling/projects/${scheduleProjectId}/tasks`);
          if (tasksResponse.data) {
            setTasks(tasksResponse.data);
          }
        }
      } catch (err) {
        console.error('Background recalculation failed:', err);
      } finally {
        setIsRecalculating(false);
      }
    }, 500);
  };

  const indentTask = async (taskId) => {
    try {
      const response = await api.post(`/scheduling/tasks/${taskId}/indent`);
      toast.success('Task indented');
      
      // Update local state with response
      if (response.data) {
        setTasks(prev => prev.map(t => t.id === taskId ? response.data : t));
      }
      
      triggerRecalculation();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Cannot indent task'));
    }
  };

  const outdentTask = async (taskId) => {
    try {
      const response = await api.post(`/scheduling/tasks/${taskId}/outdent`);
      toast.success('Task outdented');
      
      // Update local state with response
      if (response.data) {
        setTasks(prev => prev.map(t => t.id === taskId ? response.data : t));
      }
      
      triggerRecalculation();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Cannot outdent task'));
    }
  };

  const calculateSchedule = async () => {
    try {
      setCalculating(true);
      const res = await api.post(`/scheduling/projects/${scheduleProjectId}/calculate-schedule`);
      toast.success(`Schedule calculated: ${res.data.tasks_updated} tasks, ${res.data.critical_path_count} critical`);
      
      // Fetch only tasks instead of full project refresh
      const tasksRes = await api.get(`/scheduling/projects/${scheduleProjectId}/tasks`);
      if (tasksRes.data) {
        setTasks(tasksRes.data);
      }
    } catch (err) {
      toast.error('Failed to calculate schedule');
    } finally {
      setCalculating(false);
    }
  };

  // ============================================
  // EDITING FUNCTIONS
  // ============================================
  const startEditing = (taskId, field, value) => {
    const task = tasks.find(t => t.id === taskId);
    
    // Check if field is editable for this task type
    if (task && !isFieldEditable(task, field)) {
      toast.error(`This field is read-only for ${task.is_summary ? 'summary tasks' : 'milestones'}`);
      return;
    }
    
    setEditingCell({ taskId, field });
    setEditValue(value?.toString() || '');
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  const saveEdit = async () => {
    if (!editingCell) return;
    const { taskId, field } = editingCell;
    const task = tasks.find(t => t.id === taskId);
    let updates = {};
    
    // Prevent editing restricted fields for summary tasks
    if (task?.is_summary && ['percent_complete', 'weightage', 'duration', 'scheduled_start', 'scheduled_finish', 'predecessors'].includes(field)) {
      toast.error('This field is auto-calculated for summary tasks');
      setEditingCell(null);
      setEditValue('');
      return;
    }
    
    switch (field) {
      case 'name': updates.name = editValue; break;
      case 'duration': 
        // parseDuration converts days to hours (backend format)
        // Always store as hours with hours unit
        updates.duration = parseDuration(editValue);
        updates.duration_unit = 'hours';
        break;
      case 'percent_complete': 
        updates.percent_complete = Math.min(100, Math.max(0, parseFloat(editValue) || 0)); 
        break;
      case 'weightage':
        updates.weightage = Math.min(100, Math.max(0, parseFloat(editValue) || 0));
        break;
      case 'scheduled_start': updates.scheduled_start = editValue; break;
      case 'scheduled_finish': updates.scheduled_finish = editValue; break;
      case 'actual_start': 
        // Handle actual start date - can be set to null to clear
        updates.actual_start = editValue || null;
        break;
      case 'actual_finish': 
        // Handle actual finish date - can be set to null to clear
        updates.actual_finish = editValue || null;
        break;
      case 'weight': updates.weight = parseFloat(editValue) || 0; break;
      case 'predecessors':
        const deps = parsePredecessorString(editValue);
        updates.dependencies = deps;
        break;
      default: break;
    }
    
    if (Object.keys(updates).length > 0) {
      await updateTask(taskId, updates);
    }
    setEditingCell(null);
    setEditValue('');
  };
  
  // Toggle milestone status
  const toggleMilestone = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    
    if (task.is_summary) {
      toast.error('Summary tasks cannot be milestones');
      return;
    }
    
    const newValue = !task.is_milestone;
    const updates = { is_milestone: newValue };
    
    // When making a milestone, set duration to 0
    if (newValue) {
      updates.duration = 0;
    }
    
    await updateTask(taskId, updates);
  };
  
  // Update duration with unit
  const updateDurationWithUnit = async (taskId, duration, unit) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.is_summary || task.is_milestone) return;
    
    await updateTask(taskId, { duration, duration_unit: unit });
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const parsePredecessorString = (str) => {
    if (!str) return [];
    const deps = [];
    const parts = str.split(',').map(s => s.trim()).filter(Boolean);
    for (const part of parts) {
      const match = part.match(/^(\d+)(FS|SS|FF|SF)?([+-]\d+[hd]?)?$/i);
      if (match) {
        const uid = parseInt(match[1]);
        const task = tasks.find(t => t.uid === uid);
        if (task) {
          const typeMap = { 'FS': 'finish_to_start', 'SS': 'start_to_start', 'FF': 'finish_to_finish', 'SF': 'start_to_finish' };
          const type = match[2] ? typeMap[match[2].toUpperCase()] : 'finish_to_start';
          let lag = 0;
          if (match[3]) {
            const lagMatch = match[3].match(/([+-])(\d+)([hd])?/i);
            if (lagMatch) {
              lag = parseInt(lagMatch[2]) * (lagMatch[1] === '-' ? -1 : 1);
              if (lagMatch[3]?.toLowerCase() === 'd') lag *= 8;
            }
          }
          deps.push({ predecessor_id: task.id, type, lag });
        }
      }
    }
    return deps;
  };

  // ============================================
  // CLIPBOARD OPERATIONS
  // ============================================
  const copyTask = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      setClipboard({ action: 'copy', task });
      toast.success('Task copied');
    }
  };

  const cutTask = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      setClipboard({ action: 'cut', task });
      toast.success('Task cut');
    }
  };

  const pasteTask = async () => {
    if (!clipboard) return;
    try {
      const newTaskData = {
        project_id: scheduleProjectId,
        name: clipboard.task.name + (clipboard.action === 'copy' ? ' (Copy)' : ''),
        duration: clipboard.task.duration,
        parent_id: clipboard.task.parent_id
      };
      const response = await api.post('/scheduling/tasks', newTaskData);
      
      // Optimistic update - add new task to local state
      if (response.data) {
        setTasks(prev => [...prev, response.data]);
      }
      
      if (clipboard.action === 'cut') {
        await api.delete(`/scheduling/tasks/${clipboard.task.id}`);
        // Remove cut task from local state
        setTasks(prev => prev.filter(t => t.id !== clipboard.task.id));
        setClipboard(null);
      }
      
      toast.success('Task pasted');
      // Trigger background recalculation
      triggerRecalculation();
    } catch (err) {
      toast.error('Failed to paste');
    }
  };

  // ============================================
  // ROW INSERTION & BULK PASTE
  // ============================================
  
  /**
   * Insert new row(s) at a specific position
   * @param {string} referenceTaskId - The task ID relative to which we're inserting
   * @param {string} position - 'above' or 'below'
   * @param {Object} taskData - Optional task data to pre-populate
   * @param {number} count - Number of rows to insert (default 1)
   */
  const insertRow = async (referenceTaskId, position = 'below', taskData = null, count = 1) => {
    try {
      const response = await api.post(`/scheduling/projects/${scheduleProjectId}/tasks/insert-row`, {
        reference_task_id: referenceTaskId,
        position,
        task_data: taskData,
        count
      });
      
      if (response.data?.tasks) {
        setTasks(response.data.tasks);
        toast.success(`${count} row${count > 1 ? 's' : ''} inserted ${position}`);
        
        // Focus on first inserted row
        if (response.data.inserted_task_ids?.length > 0) {
          const firstNewId = response.data.inserted_task_ids[0];
          setTimeout(() => {
            setSelectedRow(firstNewId);
          }, 100);
        }
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to insert row'));
    }
  };

  /**
   * Bulk paste multiple rows from clipboard at a specific position
   * @param {string} referenceTaskId - The task ID relative to which we're inserting
   * @param {string} position - 'above' or 'below'
   * @param {Array} tasksData - Array of task data to insert
   */
  const bulkPasteRows = async (referenceTaskId, position = 'below', tasksData = []) => {
    if (!tasksData || tasksData.length === 0) {
      toast.error('No data to paste');
      return;
    }

    try {
      const response = await api.post(`/scheduling/projects/${scheduleProjectId}/tasks/bulk-paste`, {
        reference_task_id: referenceTaskId,
        position,
        tasks_data: tasksData
      });
      
      if (response.data?.tasks) {
        setTasks(response.data.tasks);
        toast.success(`${response.data.inserted_count} rows pasted`);
        
        // Focus on first inserted row
        if (response.data.inserted_task_ids?.length > 0) {
          const firstNewId = response.data.inserted_task_ids[0];
          setTimeout(() => {
            setSelectedRow(firstNewId);
          }, 100);
        }
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to paste rows'));
    }
  };

  // ============================================
  // KEYBOARD NAVIGATION
  // ============================================
  useEffect(() => {
    const handleKeyDown = (e) => {
      // ── Guard: never treat keys as WBS shortcuts while the user is editing a
      // text field, has a contenteditable focused, or a modal/dialog/panel is
      // open. Without this, pressing Delete/Enter/Insert etc. while typing in
      // the AI Schedule panel (or any other dialog) would fire deleteTask /
      // addTask on the WBS — a data-loss bug flagged by iteration_200.
      const t = e.target;
      const tag = t?.tagName || '';
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || t?.isContentEditable) return;
      if (document.querySelector('[role="dialog"], [data-radix-portal]')) return;

      // Only handle on WBS tab
      if (activeTab !== 'wbs') return;
      
      // If editing
      if (editingCell) {
        if (e.key === 'Enter') {
          e.preventDefault();
          saveEdit();
          // Move to next row and add new if at end
          const currentTasks = getVisibleTasks();
          if (focusedRow < currentTasks.length - 1) {
            setFocusedRow(focusedRow + 1);
            const nextTask = currentTasks[focusedRow + 1];
            if (nextTask) setSelectedRow(nextTask.id);
          } else {
            // Add new task at end
            addTask(currentTasks[currentTasks.length - 1]?.id);
          }
        } else if (e.key === 'Escape') {
          cancelEdit();
        } else if (e.key === 'Tab') {
          e.preventDefault();
          saveEdit();
          // Move to next editable column
          const editableCols = columns.filter(c => c.editable);
          const currentColField = editingCell.field;
          const currentIdx = editableCols.findIndex(c => c.id === currentColField);
          if (currentIdx < editableCols.length - 1) {
            const nextCol = editableCols[currentIdx + 1];
            const task = tasks.find(t => t.id === editingCell.taskId);
            if (task && nextCol) {
              const colIdx = columns.findIndex(c => c.id === nextCol.id);
              setFocusedCol(colIdx);
              startEditing(task.id, nextCol.id, getCellValue(task, nextCol.id));
            }
          }
        }
        return;
      }
      
      // Grid navigation (not editing)
      const currentTasks = getVisibleTasks();
      const task = currentTasks[focusedRow];
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (focusedRow < currentTasks.length - 1) {
            const newRow = focusedRow + 1;
            const newTaskId = currentTasks[newRow]?.id;
            
            // Shift+ArrowDown for multi-select
            if (e.shiftKey) {
              setSelectedRows(prev => {
                const newSet = new Set(prev);
                // Add current row if not already selected
                if (task?.id) newSet.add(task.id);
                // Add new row
                if (newTaskId) newSet.add(newTaskId);
                return newSet;
              });
            } else {
              // Clear multi-selection on regular navigation
              setSelectedRows(new Set());
            }
            
            setFocusedRow(newRow);
            setSelectedRow(newTaskId);
          }
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          if (focusedRow > 0) {
            const newRow = focusedRow - 1;
            const newTaskId = currentTasks[newRow]?.id;
            
            // Shift+ArrowUp for multi-select
            if (e.shiftKey) {
              setSelectedRows(prev => {
                const newSet = new Set(prev);
                // Add current row if not already selected
                if (task?.id) newSet.add(task.id);
                // Add new row
                if (newTaskId) newSet.add(newTaskId);
                return newSet;
              });
            } else {
              // Clear multi-selection on regular navigation
              setSelectedRows(new Set());
            }
            
            setFocusedRow(newRow);
            setSelectedRow(newTaskId);
          }
          break;
          
        case 'ArrowRight':
          e.preventDefault();
          setFocusedCol(Math.min(focusedCol + 1, columns.length - 1));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setFocusedCol(Math.max(focusedCol - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          // Add new task at end or start editing if at last row
          if (focusedRow === currentTasks.length - 1 || currentTasks.length === 0) {
            addTask(task?.id);
          } else {
            const col = columns[focusedCol];
            if (col?.editable && task) {
              startEditing(task.id, col.id, getCellValue(task, col.id));
            }
          }
          break;
        case 'F2':
          e.preventDefault();
          const col = columns[focusedCol];
          if (col?.editable && task) {
            startEditing(task.id, col.id, getCellValue(task, col.id));
          }
          break;
        case 'Tab':
          e.preventDefault();
          if (e.shiftKey) {
            setFocusedCol(Math.max(focusedCol - 1, 0));
          } else {
            setFocusedCol(Math.min(focusedCol + 1, columns.length - 1));
          }
          break;
        case 'Delete':
          e.preventDefault();
          if (task) deleteTask(task.id);
          break;
        case 'Insert':
          e.preventDefault();
          addTask(task?.id);
          break;
        default:
          // Ctrl/Cmd shortcuts
          if (e.ctrlKey || e.metaKey) {
            if (e.key === 'c') {
              e.preventDefault();
              // Copy cell value if focused on a cell
              const focusedColumn = columns[focusedCol];
              if (task && focusedColumn) {
                const cellValue = getCellValue(task, focusedColumn.id);
                // Store in clipboard: { type: 'cell', field, value, taskId } or { type: 'task', task }
                setClipboard({ 
                  type: 'cell', 
                  field: focusedColumn.id, 
                  value: cellValue,
                  taskId: task.id
                });
                // Also copy to system clipboard
                try {
                  navigator.clipboard.writeText(String(cellValue || ''));
                  toast.success(`Copied: ${cellValue || '(empty)'}`);
                } catch (err) {
                  toast.success(`Copied cell value`);
                }
              }
            }
            if (e.key === 'x') { 
              e.preventDefault(); 
              if (task) cutTask(task.id); 
            }
            if (e.key === 'v') {
              e.preventDefault();
              // Paste cell value if clipboard contains a cell
              if (clipboard?.type === 'cell') {
                const focusedColumn = columns[focusedCol];
                // Only paste to same column type or to selected rows
                if (task && focusedColumn?.id === clipboard.field && focusedColumn.editable) {
                  // Paste to selected rows or just current row
                  const targetRows = selectedRows.size > 0 ? Array.from(selectedRows) : [task.id];
                  
                  for (const targetId of targetRows) {
                    updateTask(targetId, { [clipboard.field]: clipboard.value }, true);
                  }
                  
                  if (targetRows.length > 1) {
                    toast.success(`Pasted to ${targetRows.length} rows`);
                  } else {
                    toast.success(`Pasted: ${clipboard.value || '(empty)'}`);
                  }
                  // Trigger recalc after all updates
                  triggerRecalculation();
                }
              } else {
                // Default paste task behavior
                pasteTask();
              }
            }
            if (e.key === 's') { e.preventDefault(); calculateSchedule(); }
          }
          // Fn+D for indent (right) - using 'd' key
          if (e.key === 'd' || e.key === 'D') {
            if (e.altKey || e.metaKey) {
              e.preventDefault();
              if (task) indentTask(task.id);
            }
          }
          // Fn+A for outdent (left) - using 'a' key
          if (e.key === 'a' || e.key === 'A') {
            if (e.altKey || e.metaKey) {
              e.preventDefault();
              if (task) outdentTask(task.id);
            }
          }
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, editingCell, focusedRow, focusedCol, tasks, clipboard, selectedRows]);

  // Get cell value for display/edit
  const getCellValue = (task, field) => {
    // Dynamic custom columns from MPP extended attributes (Text1/Number1/…).
    if (field && field.startsWith('custom_')) {
      const key = field.slice(7);
      const val = task.custom_fields?.[key];
      if (val == null || val === '') return '';
      if (typeof val === 'boolean') return val ? '✓' : '';
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return formatDate(val);
      if (typeof val === 'number') return Number.isInteger(val) ? String(val) : val.toFixed(2);
      return String(val);
    }
    switch (field) {
      case 'is_milestone': return task.is_milestone ? '◆' : '';
      case 'duration': 
        if (task.is_milestone) return '0';
        // Backend stores duration in hours, display in days
        // Use duration_unit to know if it's already in days or hours
        return formatDuration(task.duration, task.duration_unit || 'hours');
      case 'scheduled_start': return formatDate(task.scheduled_start || task.early_start);
      case 'scheduled_finish': return formatDate(task.scheduled_finish || task.early_finish);
      case 'baseline_start': return formatDate(task.baseline_start);
      case 'baseline_finish': return formatDate(task.baseline_finish);
      case 'actual_start': return formatDate(task.actual_start);
      case 'actual_finish': return formatDate(task.actual_finish);
      case 'predecessors': return formatPredecessors(task.dependencies, tasks);
      case 'calendar_id':
        const cal = calendars.find(c => c.id === task.calendar_id);
        return cal?.name || '';
      case 'resources': 
        const assignedResources = task.resource_assignments?.map(a => {
          // MPP imports store assignments as objects {resource_id, units, ...};
          // legacy creations stored a plain id string. Support both.
          const rid = typeof a === 'string' ? a : (a && a.resource_id);
          const resource = resources.find(r => r.id === rid);
          return resource?.initials || resource?.name?.substring(0, 2);
        }).filter(Boolean) || [];
        return assignedResources.length > 0 ? assignedResources.join(', ') : '';
      case 'constraint_type':
        const constraintLabels = {
          'as_soon_as_possible': 'ASAP',
          'as_late_as_possible': 'ALAP',
          'must_start_on': 'MSO',
          'must_finish_on': 'MFO',
          'start_no_earlier_than': 'SNET',
          'start_no_later_than': 'SNLT',
          'finish_no_earlier_than': 'FNET',
          'finish_no_later_than': 'FNLT'
        };
        return constraintLabels[task.constraint_type] || 'ASAP';
      case 'weightage': 
        const wgt = task.weightage !== undefined ? task.weightage : (task.weight || 0);
        return task.is_summary ? `${wgt.toFixed(1)}%` : `${wgt}%`;
      case 'percent_complete': 
        const pct = task.percent_complete !== undefined ? task.percent_complete : 0;
        return task.is_summary ? `${pct.toFixed(1)}%` : `${pct}%`;
      default: return task[field] || '';
    }
  };

  // Context menu
  const handleContextMenu = (e, task) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, task });
  };

  // Baseline operations
  const saveBaseline = async () => {
    if (!newBaselineName.trim()) { toast.error('Enter baseline name'); return; }
    try {
      const response = await api.post(`/scheduling/projects/${scheduleProjectId}/baselines`, { name: newBaselineName });
      toast.success('Baseline saved');
      setShowBaselineDialog(false);
      setNewBaselineName('');
      
      // Optimistic update - add new baseline to local state
      if (response.data) {
        setBaselines(prev => [...prev, response.data]);
      }
      
      // Update task baseline fields by fetching only tasks
      const tasksRes = await api.get(`/scheduling/projects/${scheduleProjectId}/tasks`);
      if (tasksRes.data) {
        setTasks(tasksRes.data);
      }
    } catch (err) {
      toast.error('Failed to save baseline');
    }
  };

  const compareBaseline = async (num) => {
    try {
      const res = await api.get(`/scheduling/projects/${scheduleProjectId}/baselines/${num}/compare`);
      setBaselineComparison(res.data);
    } catch (err) {
      toast.error('Failed to compare baseline');
    }
  };

  // Create baseline from toolbar
  const createBaselineFromToolbar = async () => {
    try {
      const baselineName = `Baseline ${(baselines?.length || 0) + 1} - ${new Date().toLocaleDateString()}`;
      const response = await api.post(`/scheduling/projects/${scheduleProjectId}/baselines`, { name: baselineName });
      toast.success('Baseline created! Start/Finish dates are now Forecast dates.');
      
      // Optimistic update - add new baseline to local state
      if (response.data) {
        setBaselines(prev => [...prev, response.data]);
      }
      
      // Update task baseline fields by fetching only tasks
      const tasksRes = await api.get(`/scheduling/projects/${scheduleProjectId}/tasks`);
      if (tasksRes.data) {
        setTasks(tasksRes.data);
      }
    } catch (err) {
      toast.error('Failed to create baseline');
    }
  };

  // ============================================
  // DRAG AND DROP
  // ============================================
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
  };

  const handleDragOver = (e, task) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedTask && task.id !== draggedTask.id) {
      setDropTarget(task.id);
    }
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (e, targetTask) => {
    e.preventDefault();
    if (!draggedTask || draggedTask.id === targetTask.id) {
      setDraggedTask(null);
      setDropTarget(null);
      return;
    }

    try {
      // Call the reorder endpoint
      const response = await api.post(`/scheduling/tasks/${draggedTask.id}/reorder`, {
        target_task_id: targetTask.id,
        position: 'after',
        new_parent_id: targetTask.parent_id
      });
      
      // Update all tasks with the response (includes recalculated WBS codes and positions)
      if (response.data && Array.isArray(response.data)) {
        setTasks(response.data);
      }
      
      toast.success('Task reordered');
    } catch (err) {
      toast.error('Failed to reorder task');
      // Revert on error - fetch tasks only
      const tasksRes = await api.get(`/scheduling/projects/${scheduleProjectId}/tasks`);
      if (tasksRes.data) {
        setTasks(tasksRes.data);
      }
    } finally {
      setDraggedTask(null);
      setDropTarget(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDropTarget(null);
  };

  // ============================================
  // WEIGHTAGE AUTO-DISTRIBUTION (Hierarchical)
  // ============================================
  const autoDistributeWeightage = async (parentId = null) => {
    try {
      // Build tree structure for hierarchical calculation
      const taskMap = {};
      tasks.forEach(t => { taskMap[t.id] = t; });
      
      // Get all leaf tasks (non-summary tasks)
      const leafTasks = tasks.filter(t => !t.is_summary);
      
      if (leafTasks.length === 0) {
        toast.error('No leaf tasks to distribute');
        return;
      }
      
      // Calculate weights based on duration - equal distribution across all leaf tasks
      const totalDuration = leafTasks.reduce((sum, t) => sum + (t.duration || 8), 0);
      
      // Calculate and assign weights to leaf tasks
      const updatedWeightages = {};
      let totalWeight = 0;
      
      for (let i = 0; i < leafTasks.length; i++) {
        const task = leafTasks[i];
        const weight = totalDuration > 0 
          ? Math.round((task.duration || 8) / totalDuration * 100 * 10) / 10
          : Math.round(100 / leafTasks.length * 10) / 10;
        updatedWeightages[task.id] = weight;
        totalWeight += weight;
      }
      
      // Ensure weights sum to 100 (adjust last task for rounding)
      const weightDiff = 100 - totalWeight;
      if (Math.abs(weightDiff) > 0.01 && leafTasks.length > 0) {
        const lastTask = leafTasks[leafTasks.length - 1];
        updatedWeightages[lastTask.id] = Math.round((updatedWeightages[lastTask.id] + weightDiff) * 10) / 10;
      }
      
      // Calculate parent (summary) weights by summing children
      const calculateParentWeightage = (summaryId) => {
        const children = tasks.filter(t => t.parent_id === summaryId);
        let childWeightSum = 0;
        
        for (const child of children) {
          if (child.is_summary) {
            // Recursively calculate child summary weights
            childWeightSum += calculateParentWeightage(child.id);
          } else {
            childWeightSum += updatedWeightages[child.id] || 0;
          }
        }
        
        updatedWeightages[summaryId] = Math.round(childWeightSum * 10) / 10;
        return childWeightSum;
      };
      
      // Get top-level summaries and calculate their weights
      const topLevelSummaries = tasks.filter(t => t.is_summary && !t.parent_id);
      topLevelSummaries.forEach(summary => {
        calculateParentWeightage(summary.id);
      });
      
      // Also handle mid-level summaries that may have a parent
      const midLevelSummaries = tasks.filter(t => t.is_summary && t.parent_id);
      midLevelSummaries.forEach(summary => {
        if (!updatedWeightages[summary.id]) {
          calculateParentWeightage(summary.id);
        }
      });
      
      // Optimistic update - update weightages locally
      setTasks(prev => prev.map(t => 
        updatedWeightages[t.id] !== undefined 
          ? { ...t, weightage: updatedWeightages[t.id], weight: updatedWeightages[t.id] }
          : t
      ));
      
      // Single bulk round-trip for the leaf task weight assignment.
      // Sequential PUTs across 285 tasks previously exceeded the 30 s
      // Cloudflare proxy timeout and triggered a 502.
      const leafPayload = {};
      for (const task of leafTasks) {
        leafPayload[task.id] = updatedWeightages[task.id];
      }
      await api.post('/scheduling/tasks/bulk-set-weights', leafPayload);
      
      toast.success(`Weightage distributed: ${leafTasks.length} tasks, total = 100%`);
      
      // Trigger recalculation to update summary rollups
      triggerRecalculation();
    } catch (err) {
      toast.error('Failed to distribute weightage');
      console.error(err);
    }
  };
  
  // ============================================
  // SCHEDULE RECALCULATION (Enterprise Engine)
  // ============================================
  // Core recalculate implementation.  When a policy is provided (a|b|c|d)
  // it is forwarded to the backend to reforecast STARVED tasks before CPM.
  const _executeRecalculate = (changedTaskId = null, policy = null) => {
    // GUARD: WBSToolbar wires this as `onClick={onRecalculate}` which passes
    // the React SyntheticEvent as the first arg — its FiberNode has circular
    // refs that blow up JSON.stringify.  Only accept plain string ids.
    const safeChangedId = typeof changedTaskId === 'string' && changedTaskId ? changedTaskId : null;
    setTimeout(async () => {
      let progressId = null;
      let phaseTimer = null;
      try {
        setIsRecalculating(true);

        console.log('Recalculating schedule for project:', scheduleProjectId);

        // Snapshot dates BEFORE recalc so we can produce a "why-changed" diff.
        const beforeMap = {};
        const beforeProjStart = project?.start_date;
        const beforeProjFinish = project?.finish_date;
        for (const t of tasks || []) {
          beforeMap[t.id] = {
            name: t.name,
            start: t.scheduled_start,
            finish: t.scheduled_finish,
          };
        }
        const t0 = Date.now();
        const taskCount = (tasks || []).length;

        // Phase-based progress simulation.  CPM runs in a single HTTP call
        // on the backend, so we mimic the 6 engine stages proportional to
        // task count.  This gives PMs live feedback instead of a dead
        // spinner while a 5,000-task recalc chews for 30s.
        const PHASES = [
          { pct: 5,  label: 'Validating DAG (checking for circular deps)…' },
          { pct: 20, label: 'Forward pass — computing Early Start/Early Finish…' },
          { pct: 45, label: 'WBS roll-up — aggregating summary dates…' },
          { pct: 65, label: 'Backward pass — computing Late Start/Late Finish…' },
          { pct: 80, label: 'Calculating total & free float…' },
          { pct: 92, label: 'Identifying critical path…' },
        ];
        let phaseIx = 0;
        let phaseDone = false;   // stops the ticker from re-loading a finished toast
        const stopPhases = () => {
          phaseDone = true;
          if (phaseTimer) { clearInterval(phaseTimer); phaseTimer = null; }
        };
        const renderPhase = () => {
          if (phaseDone) return;
          const ph = PHASES[Math.min(phaseIx, PHASES.length - 1)];
          const desc = `${taskCount} tasks · ${ph.pct}% · ${ph.label}`;
          if (progressId) {
            toast.loading(`Running CPM${policy ? ` · policy ${policy.toUpperCase()}` : ''}…`, {
              id: progressId,
              description: desc,
            });
          }
        };
        progressId = toast.loading(`Running CPM${policy ? ` · policy ${policy.toUpperCase()}` : ''}…`, {
          description: `${taskCount} tasks · 0% · Preparing engine…`,
        });
        // Rough cadence: 1 phase every 500ms (small schedules finish before we
        // reach 92%; large ones sit on 92% until the response arrives).
        const phaseIntervalMs = Math.min(600, Math.max(200, Math.round(taskCount * 0.6)));
        phaseTimer = setInterval(() => {
          phaseIx = Math.min(phaseIx + 1, PHASES.length - 1);
          renderPhase();
        }, phaseIntervalMs);

        // Use native fetch instead of axios
        const token = localStorage.getItem('ciq_token');
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const url = `${baseUrl}/api/scheduling/projects/${scheduleProjectId}/schedule/recalculate${policy ? `?policy=${policy}` : ''}`;

        // Sanitize body: only include primitive-typed fields we control.
        const bodyObj = {};
        if (safeChangedId) { bodyObj.incremental = true; bodyObj.changedTaskId = safeChangedId; }
        if (policy) bodyObj.policy = policy;
        const bodyStr = JSON.stringify(bodyObj);
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: bodyStr
        });
        
        console.log('Fetch response status:', response.status);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `HTTP Error ${response.status}`);
        }
        
        const result = await response.json();
        console.log('Recalculate completed:', result?.status);

        // CPM is DONE the moment the response lands. Kill the phase ticker
        // right here — leaving it running meant every later tick re-painted
        // the toast as `toast.loading(... 92% Identifying critical path)`
        // AFTER the success toast, so the spinner never stopped rolling
        // while the (slow) narrative step finished.
        stopPhases();
        
        setLastRecalculation({
          timestamp: new Date(),
          stats: result?.stats || {},
          criticalPath: result?.criticalTaskIds || []
        });
        
        // Fetch updated tasks using fetch
        const tasksUrl = `${baseUrl}/api/scheduling/projects/${scheduleProjectId}/tasks`;
        const tasksRes = await fetch(tasksUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        let afterTasks = [];
        if (tasksRes.ok) {
          const tasksData = await tasksRes.json();
          afterTasks = tasksData.reduce((acc, task) => {
            if (!acc.find(t => t.id === task.id)) acc.push(task);
            return acc;
          }, []);
          setTasks(afterTasks);
        }

        // ── Analytical diff: how many dates moved + by how much ───────────
        const diffs = [];
        let movedStart = 0, movedFinish = 0, maxDelta = 0;
        for (const t of afterTasks) {
          const b = beforeMap[t.id];
          if (!b) continue;
          const dayMs = 86400000;
          const dsStart = b.start && t.scheduled_start
            ? Math.round((new Date(t.scheduled_start) - new Date(b.start)) / dayMs)
            : 0;
          const dsFinish = b.finish && t.scheduled_finish
            ? Math.round((new Date(t.scheduled_finish) - new Date(b.finish)) / dayMs)
            : 0;
          if (dsStart !== 0) movedStart++;
          if (dsFinish !== 0) movedFinish++;
          if (Math.abs(dsFinish) > Math.abs(maxDelta)) maxDelta = dsFinish;
          if (dsStart !== 0 || dsFinish !== 0) {
            diffs.push({
              taskId:  t.id,
              name:    t.name,
              wbsCode: t.wbs_code,
              is_critical: t.is_critical,
              before:  { start: b.start,  finish: b.finish },
              after:   { start: t.scheduled_start, finish: t.scheduled_finish },
              dsStart, dsFinish,
            });
          }
        }
        diffs.sort((a, b) => Math.abs(b.dsFinish) - Math.abs(a.dsFinish));
        const ms = Date.now() - t0;
        const explanation = diffs.length === 0
          ? 'No task dates changed — your imported plan was already consistent with its dependencies and constraints.'
          : `${diffs.length} tasks shifted (${movedStart} starts · ${movedFinish} finishes). Largest movement: ${diffs[0].name?.slice(0, 32) || '—'} by ${diffs[0].dsFinish > 0 ? '+' : ''}${diffs[0].dsFinish} day(s). CPM pulls dates forward when predecessors finish later than expected, and pushes finishes when a task's duration cannot fit in working time.`;

        // Open the slide-in diff panel so PMs can defend the change
        setRecalcDiffPanel({
          open: true,
          summary: {
            movedStart, movedFinish,
            criticalCount: result?.criticalPathCount || result?.critical_path_count || 0,
            elapsedMs: ms,
          },
          diffs,
        });

        toast.success(`CPM recalculated · ${result?.updatedTasks || result?.tasks_updated || 0} tasks · ${result?.criticalPathCount || result?.critical_path_count || 0} on critical path · ${(ms / 1000).toFixed(1)} s`, {
          id: progressId,
          duration: 10000,
          description: explanation,
        });

        // ── Undo Policy chip + Cascade viz — only after a policy apply
        if (policy && result?.policySnapshotId) {
          const snapId = result.policySnapshotId;
          toast.success(`Policy ${policy.toUpperCase()} applied · ${result?.policyMetrics?.tasks_moved || 0} tasks re-forecast`, {
            duration: 30000,
            action: {
              label: 'Undo',
              onClick: async () => {
                const undoId = toast.loading('Reverting policy…');
                try {
                  const res = await api.post(`/scheduling/projects/${scheduleProjectId}/schedule/undo-policy/${snapId}`);
                  toast.success(`Policy reverted · ${res.data?.tasksRestored || 0} tasks restored`, { id: undoId });
                  const tasksRes = await api.get(`/scheduling/projects/${scheduleProjectId}/tasks`);
                  if (tasksRes.data) setTasks(tasksRes.data);
                  setCascade({ open: false, loading: false, payload: null, snapshotId: null });
                } catch (e) { toast.error('Undo failed', { id: undoId }); }
              },
            },
          });
          // Fetch cascade for the mini-network panel
          setCascade({ open: false, loading: true, payload: null, snapshotId: snapId });
          try {
            const cRes = await api.get(`/scheduling/projects/${scheduleProjectId}/schedule/cascade/${snapId}`);
            setCascade({ open: true, loading: false, payload: cRes.data, snapshotId: snapId });
          } catch (e) { setCascade({ open: false, loading: false, payload: null, snapshotId: null }); }
        }

        // ── Cognara: author the Schedule Impact Narrative + (optionally)
        //    raise a RAID delay-risk for sponsor visibility.
        if (diffs.length > 0) {
          try {
            const critIds = new Set(result?.criticalTaskIds || result?.critical_task_ids || []);
            const beforeFinishProj = beforeProjFinish;
            const afterFinishProj = afterTasks.length
              ? afterTasks.map(t => t.scheduled_finish).filter(Boolean).sort().slice(-1)[0]
              : beforeProjFinish;
            const payload = {
              before_finish: beforeFinishProj,
              after_finish: afterFinishProj,
              critical_count: result?.criticalPathCount || result?.critical_path_count || 0,
              warnings: result?.warnings || [],
              auto_raise_raid: true,
              portfolio_project_id: parentProjectId,
              deltas: afterTasks
                .map((t) => {
                  const b = beforeMap[t.id];
                  if (!b) return null;
                  const dayMs = 86400000;
                  const ds = b.start && t.scheduled_start
                    ? Math.round((new Date(t.scheduled_start) - new Date(b.start)) / dayMs)
                    : 0;
                  const df = b.finish && t.scheduled_finish
                    ? Math.round((new Date(t.scheduled_finish) - new Date(b.finish)) / dayMs)
                    : 0;
                  if (ds === 0 && df === 0) return null;
                  return {
                    id: t.id,
                    name: t.name,
                    wbs_code: t.wbs_code || t.wbsCode || t.wbs || '',
                    ds_start_days: ds,
                    ds_finish_days: df,
                    is_critical: critIds.has(t.id),
                  };
                })
                .filter(Boolean),
            };
            const { data: narrRes } = await api.post(
              `/scheduling/projects/${scheduleProjectId}/recalculate-narrative`,
              payload,
            );
            setImpactReport({
              when: new Date().toISOString(),
              narrative: narrRes.narrative,
              impacted: narrRes.impacted_tasks || [],
              critical_shifted_count: narrRes.critical_shifted_count || 0,
              largest_finish_shift_days: narrRes.largest_finish_shift_days || 0,
              raid_item: narrRes.raid_item || null,
              before_finish: payload.before_finish,
              after_finish: payload.after_finish,
            });
            // Persist so the analysis survives tab switches / refreshes
            // and can be re-opened via "View Last Analysis" any time.
            try {
              localStorage.setItem(
                `cpm-analysis:${scheduleProjectId}`,
                JSON.stringify({
                  when: new Date().toISOString(),
                  narrative: narrRes.narrative,
                  impacted: narrRes.impacted_tasks || [],
                  critical_shifted_count: narrRes.critical_shifted_count || 0,
                  largest_finish_shift_days: narrRes.largest_finish_shift_days || 0,
                  raid_item: narrRes.raid_item || null,
                  before_finish: payload.before_finish,
                  after_finish: payload.after_finish,
                }),
              );
            } catch { /* localStorage quota — ignore */ }
            setImpactDialogOpen(true);
            if (narrRes.raid_item?.raidNumber) {
              toast.info(`Cognara filed RAID ${narrRes.raid_item.raidNumber}`, {
                description: 'Critical-path slip raised as a delay risk for sponsor visibility.',
                duration: 8000,
              });
            }
          } catch (e) {
            console.warn('Narrative/RAID step failed (non-fatal):', e);
          }
        }
      } catch (err) {
        console.log('Recalculate error:', String(err?.message || err));
        // Dismiss the loading toast so users don't see it "hang" forever
        // — critical, otherwise the shimmering spinner never leaves the DOM.
        if (phaseTimer) { clearInterval(phaseTimer); phaseTimer = null; }
        const msg = String(err?.message || err || 'Failed to recalculate schedule');
        if (progressId) {
          toast.error(msg, { id: progressId, duration: 8000 });
        } else {
          toast.error(msg);
        }
      } finally {
        if (phaseTimer) { clearInterval(phaseTimer); phaseTimer = null; }
        setIsRecalculating(false);
      }
    }, 0);
  };

  // Public entrypoint bound to the toolbar / grid.  Always opens the
  // 4-Policy modal so the user consciously picks a policy (or "Skip &
  // just Recalculate").  Only silently falls back to CPM if the preview
  // HTTP call itself blows up — otherwise the user is always in control.
  const recalculateSchedule = async (changedTaskId = null) => {
    // GUARD: WBSToolbar's `onClick={onRecalculate}` passes the React
    // SyntheticEvent as first arg.  Only accept plain string ids.
    const safeChangedId = typeof changedTaskId === 'string' && changedTaskId ? changedTaskId : null;
    if (!scheduleProjectId || typeof scheduleProjectId !== 'string') {
      toast.error('No project selected');
      return;
    }
    // Open the modal immediately in loading state
    setPolicyModal({ open: true, loading: true, applying: false, preview: null, changedTaskId: safeChangedId });
    try {
      const token = localStorage.getItem('ciq_token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(
        `${baseUrl}/api/scheduling/projects/${scheduleProjectId}/schedule/recalculate-preview`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const preview = await res.json();
      // ALWAYS show the modal — even when starved_count is 0 the user
      // may want to review before running CPM (or use "Skip Policy").
      setPolicyModal({ open: true, loading: false, applying: false, preview, changedTaskId: safeChangedId });
    } catch (err) {
      console.warn('Recalculate preview failed:', err);
      // Preview endpoint broken → close modal and toast a clear error
      // instead of silently running CPM without the user's consent.
      setPolicyModal({ open: false, loading: false, applying: false, preview: null, changedTaskId: null });
      toast.error(`Failed to load recalculate preview: ${err?.message || err}`, { duration: 6000 });
    }
  };

  const applyRecalcPolicy = (policyKey) => {
    setPolicyModal(prev => ({ ...prev, applying: true }));
    const changedTaskId = policyModal.changedTaskId;
    // Close modal on next tick after firing the recalc so the busy state
    // is visible while CPM runs.
    setTimeout(() => {
      setPolicyModal({ open: false, loading: false, applying: false, preview: null, changedTaskId: null });
    }, 250);
    _executeRecalculate(changedTaskId, policyKey);
  };

  // "Skip Policy · Just Recalculate" — runs CPM without applying any of
  // the 4 forecast policies.  Used when the user wants a pure critical-
  // path recompute (or there are no starved tasks).
  const skipRecalcPolicy = () => {
    const changedTaskId = policyModal.changedTaskId;
    setPolicyModal({ open: false, loading: false, applying: false, preview: null, changedTaskId: null });
    _executeRecalculate(changedTaskId, null);
  };
  
  // Validate schedule DAG
  const validateScheduleDAG = async () => {
    try {
      const response = await api.post(
        `/scheduling/projects/${scheduleProjectId}/schedule/validate`
      );
      
      if (response.data.isValid) {
        toast.success('Schedule is valid - no circular dependencies detected');
      } else {
        toast.error(`Circular dependencies detected in ${response.data.cycleNodes.length} tasks`);
      }
      
      return response.data;
    } catch (err) {
      toast.error('Failed to validate schedule');
      return { isValid: false };
    }
  };

  // ============================================
  // LOADING & ERROR STATES
  // ============================================
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-[#1E4E8C] border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-400">Loading schedule...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <AlertTriangle className="w-12 h-12 text-amber-500" />
          <p className="text-slate-400">Project not found</p>
          <Button onClick={() => navigate('/scheduling')}>Back to Projects</Button>
        </div>
      </DashboardLayout>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <DashboardLayout>
      <TooltipProvider>
        <RecalcDiffPanel open={recalcDiffPanel.open}
                         summary={recalcDiffPanel.summary}
                         diffs={recalcDiffPanel.diffs}
                         onClose={() => setRecalcDiffPanel(p => ({ ...p, open: false }))} />
        <RecalcPolicyModal
          open={policyModal.open}
          loading={policyModal.loading}
          applying={policyModal.applying}
          preview={policyModal.preview}
          baselineFinish={project?.finish_date}
          onClose={() => setPolicyModal({ open: false, loading: false, applying: false, preview: null, changedTaskId: null })}
          onApply={applyRecalcPolicy}
          onSkip={skipRecalcPolicy}
        />
        <CascadePanel
          open={cascade.open}
          loading={cascade.loading}
          payload={cascade.payload}
          canUndo={!!cascade.snapshotId}
          onUndo={async () => {
            if (!cascade.snapshotId) return;
            const undoId = toast.loading('Reverting policy…');
            try {
              const res = await api.post(`/scheduling/projects/${scheduleProjectId}/schedule/undo-policy/${cascade.snapshotId}`);
              toast.success(`Policy reverted · ${res.data?.tasksRestored || 0} tasks restored`, { id: undoId });
              const tasksRes = await api.get(`/scheduling/projects/${scheduleProjectId}/tasks`);
              if (tasksRes.data) setTasks(tasksRes.data);
              setCascade({ open: false, loading: false, payload: null, snapshotId: null });
            } catch (e) { toast.error('Undo failed', { id: undoId }); }
          }}
          onClose={() => setCascade({ open: false, loading: false, payload: null, snapshotId: null })}
        />
        <ScheduleVersionsDrawer
          open={versionsDrawerOpen}
          onClose={() => setVersionsDrawerOpen(false)}
          projectId={scheduleProjectId}
        />
        <div className="space-y-4" onClick={() => setContextMenu(null)}>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/scheduling')} className="text-slate-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">{project.name}</h1>
                {project.description && <p className="text-sm text-slate-400">{project.description}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* AI Generate Button */}
              <Button 
                onClick={() => setShowAIPanel(true)} 
                variant="outline"
                className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10 hover:text-purple-200"
                data-testid="ai-generate-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI Generate
              </Button>
              <Button onClick={calculateSchedule} disabled={calculating} className="bg-gradient-to-r from-[#1E4E8C] to-[#5B78FF]">
                {calculating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
                Calculate Schedule
              </Button>
            </div>
          </div>

          {/* AI Analyst — scoped to this macro schedule */}
          {parentProjectId && (
            <AIAnalystPanel projectId={parentProjectId} moduleKey="MACRO_SCHEDULE" defaultOpen={false} />
          )}

          {/* Milestone strip — always visible above the tabs */}
          <MilestoneBar projectId={parentProjectId || project?.id} />

          {/* Main Tabs - MS Project Style */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-[#0c1222] border border-slate-700/50 p-0.5 h-auto rounded-lg">
              <TabsTrigger value="wbs" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white gap-2 px-4 py-2 rounded-md" data-testid="tab-wbs">
                <FileSpreadsheet className="w-4 h-4" /> WBS / Tasks
              </TabsTrigger>
              <TabsTrigger value="gantt" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white gap-2 px-4 py-2 rounded-md" data-testid="tab-gantt">
                <BarChart3 className="w-4 h-4" /> Gantt
              </TabsTrigger>
              <TabsTrigger value="baselines" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white gap-2 px-4 py-2 rounded-md" data-testid="tab-baselines">
                <Flag className="w-4 h-4" /> Baselines
              </TabsTrigger>
              <TabsTrigger value="calendars" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white gap-2 px-4 py-2 rounded-md" data-testid="tab-calendars">
                <Calendar className="w-4 h-4" /> Calendars
              </TabsTrigger>
              <TabsTrigger value="resources" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white gap-2 px-4 py-2 rounded-md" data-testid="tab-resources">
                <Users className="w-4 h-4" /> Resources
              </TabsTrigger>
              <TabsTrigger value="evm" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white gap-2 px-4 py-2 rounded-md" data-testid="tab-evm">
                <TrendingUp className="w-4 h-4" /> EVM Metrics
              </TabsTrigger>
              <TabsTrigger value="scurve" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white gap-2 px-4 py-2 rounded-md" data-testid="tab-scurve">
                <LineChart className="w-4 h-4" /> S-Curve
              </TabsTrigger>
              <TabsTrigger value="risk" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white gap-2 px-4 py-2 rounded-md" data-testid="tab-risk">
                <Activity className="w-4 h-4" /> Risk Analysis
              </TabsTrigger>
              <TabsTrigger value="rollup" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white gap-2 px-4 py-2 rounded-md" data-testid="tab-rollup">
                <Sparkles className="w-4 h-4" /> Roll-Up
              </TabsTrigger>
              <TabsTrigger value="insights" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white gap-2 px-4 py-2 rounded-md" data-testid="tab-insights">
                <BarChart3 className="w-4 h-4" /> Insights
              </TabsTrigger>
              <TabsTrigger value="ai-ops" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white gap-2 px-4 py-2 rounded-md" data-testid="tab-ai-ops">
                <Sparkles className="w-4 h-4" /> AI Ops
              </TabsTrigger>
            </TabsList>

            {/* WBS / Tasks Tab - AG Grid */}
            <TabsContent value="wbs" className="mt-4">
              {/* Status strip — CPM mode toggle + persistent "View Last Analysis". */}
              <div className="flex justify-between items-center mb-2 gap-3">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500">CPM mode:</span>
                  <button
                    onClick={() => setCpmAutoMode(v => !v)}
                    className={
                      'px-2 py-0.5 rounded-full border text-[11px] ' +
                      (cpmAutoMode
                        ? 'bg-amber-500/10 text-amber-300 border-amber-500/40'
                        : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40')
                    }
                    data-testid="cpm-mode-toggle"
                    title={cpmAutoMode
                      ? 'Auto: CPM runs after every edit. Imported MPP dates may shift.'
                      : 'Manual: imported MPP dates are frozen until you click Recalculate.'}
                  >
                    {cpmAutoMode ? 'AUTO' : 'MANUAL (imported dates frozen)'}
                  </button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => impactReport && setImpactDialogOpen(true)}
                  disabled={!impactReport}
                  className={`gap-2 ${impactReport
                    ? 'border-violet-500/40 bg-violet-500/5 text-violet-300 hover:bg-violet-500/10'
                    : 'border-slate-700 text-slate-500'}`}
                  data-testid="view-last-analysis-btn"
                  title={impactReport
                    ? 'Reopen the Cognara Schedule Impact narrative from the last Recalculate'
                    : 'Run Recalculate (CPM) to generate an analysis'}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  View Last Analysis
                  {impactReport && impactReport.critical_shifted_count > 0 && (
                    <Badge variant="outline" className="ml-1 border-red-500/50 text-red-300 text-[10px] h-4 px-1">
                      {impactReport.critical_shifted_count} critical
                    </Badge>
                  )}
                </Button>
              </div>
              <div style={{ height: 'calc(100vh - 280px)', width: '100%' }}>
                <AGGridWBS
                  tasks={tasks}
                  calendars={calendars}
                  resources={resources}
                  project={project}
                  parentProjectId={parentProjectId}
                  scheduleId={scheduleProjectId}
                  baselines={baselines}
                  activeBaseline={baselines?.find(b => b.is_active)}
                  customFieldSchema={customFieldSchema}
                  selectedCustomCols={selectedCustomCols}
                  setSelectedCustomCols={setSelectedCustomCols}
                  onTaskUpdate={updateTask}
                  onTaskAdd={(taskId, position) => {
                    // Handle context menu positions: 'above', 'below', 'child'
                    if (position === 'child') {
                      // Insert as child of selected task
                      addTask(null, taskId);
                    } else if (position === 'above') {
                      // Insert above - find previous sibling
                      const task = tasks.find(t => t.id === taskId);
                      if (task) {
                        addTask(taskId, task.parent_id);
                      } else {
                        addTask(taskId);
                      }
                    } else {
                      // Default: insert below selected task
                      addTask(taskId);
                    }
                  }}
                  onTaskDelete={deleteTask}
                  onAttachDocs={(task) => setAttachDocsForTask(task)}
                  attachmentCounts={taskAttachmentCounts}
                  onDeleteEntireSchedule={() => setShowDeleteScheduleDialog(true)}
                  onBulkAttachDocs={(taskIds) => {
                    if (!taskIds || taskIds.length < 2) {
                      toast.error('Select 2+ activities to bulk-attach documents');
                      return;
                    }
                    setBulkAttachTaskIds(taskIds);
                    setShowBulkAttachPicker(true);
                  }}
                  onIndent={indentTask}
                  onOutdent={outdentTask}
                  onCopyTask={copyTask}
                  onPasteTask={(afterTaskId, taskData) => pasteTask(afterTaskId)}
                  onInsertRow={insertRow}
                  onBulkPasteRows={bulkPasteRows}
                  onMoveToParent={(taskId, parentId) => {
                    // Move task to new parent
                    updateTask(taskId, { parent_id: parentId });
                  }}
                  onMoveToChild={(taskId, newParentId) => {
                    // Move task as child of target
                    updateTask(taskId, { parent_id: newParentId });
                  }}
                  onCreateBaseline={async (baselineData) => {
                    try {
                      await api.post(`/scheduling/projects/${scheduleProjectId}/baselines`, baselineData);
                      fetchProject();
                    } catch (error) {
                      console.error('Failed to create baseline:', error);
                    }
                  }}
                  onSetActiveBaseline={async (baselineId) => {
                    try {
                      await api.put(`/scheduling/projects/${scheduleProjectId}/baselines/${baselineId}/activate`);
                      fetchProject();
                    } catch (error) {
                      console.error('Failed to set active baseline:', error);
                    }
                  }}
                  onDeleteBaseline={async (baselineId) => {
                    if (!baselineId) return;
                    try {
                      await api.delete(`/scheduling/projects/${scheduleProjectId}/baselines/${baselineId}`);
                      fetchProject();
                    } catch (error) {
                      console.error('Failed to delete baseline:', error);
                    }
                  }}
                  onExportBaseline={() => {
                    toast.info('Export baseline feature coming soon');
                  }}
                  onRecalculate={recalculateSchedule}
                  onOpenVersions={() => setVersionsDrawerOpen(true)}
                  userRole="planner"
                />
              </div>
            </TabsContent>

            {/* Gantt Tab */}
            <TabsContent value="gantt" className="mt-4">
              <GanttView
                tasks={tasks}
                dataDate={project?.status_date || project?.data_date || null}
                height={720}
              />
            </TabsContent>

            {/* Baselines Tab */}
            <TabsContent value="baselines" className="mt-4">
              <BaselinesTab 
                baselines={baselines}
                comparison={baselineComparison}
                onSave={() => setShowBaselineDialog(true)}
                onCompare={compareBaseline}
                formatDate={formatDate}
                formatDuration={formatDuration}
              />
            </TabsContent>

            {/* Calendars Tab */}
            <TabsContent value="calendars" className="mt-4">
              <CalendarsTab projectId={scheduleProjectId} onRefresh={fetchProject} />
            </TabsContent>

            {/* Resources Tab */}
            <TabsContent value="resources" className="mt-4">
              <ResourcesTab 
                resources={resources} 
                tasks={tasks}
                projectId={scheduleProjectId} 
                onRefresh={fetchProject}
              />
            </TabsContent>

            {/* EVM Metrics Tab */}
            <TabsContent value="evm" className="mt-4">
              <EVMMetricsTab metrics={metrics} tasks={tasks} />
            </TabsContent>

            {/* S-Curve Tab */}
            <TabsContent value="scurve" className="mt-4">
              <SCurveTab data={sCurveData} scheduleProjectId={scheduleProjectId} />
            </TabsContent>

            {/* Risk Analysis Tab - Enhanced */}
            <TabsContent value="risk" className="mt-4">
              <RiskAnalysisTabEnhanced projectId={scheduleProjectId} tasks={tasks} />
            </TabsContent>
            <TabsContent value="rollup" className="mt-4">
              <RollUpTabContent projectId={scheduleProjectId} />
            </TabsContent>
            <TabsContent value="insights" className="mt-4">
              <ScheduleInsights scheduleId={scheduleProjectId} onRefresh={fetchProject} />
            </TabsContent>
            <TabsContent value="ai-ops" className="mt-4">
              <SchedulePredictOps scheduleId={scheduleProjectId} />
            </TabsContent>
          </Tabs>

          {/* Context Menu */}
          <AnimatePresence>
            {contextMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 z-50"
                style={{ left: contextMenu.x, top: contextMenu.y }}
              >
                <button className="w-full px-4 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 flex items-center gap-2" onClick={() => { addTask(contextMenu.task?.id); setContextMenu(null); }}>
                  <Plus className="w-4 h-4" /> Add Task Below
                </button>
                <button className="w-full px-4 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 flex items-center gap-2" onClick={() => { indentTask(contextMenu.task?.id); setContextMenu(null); }}>
                  <Indent className="w-4 h-4" /> Indent
                </button>
                <button className="w-full px-4 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 flex items-center gap-2" onClick={() => { outdentTask(contextMenu.task?.id); setContextMenu(null); }}>
                  <Outdent className="w-4 h-4" /> Outdent
                </button>
                <div className="h-px bg-slate-700 my-1" />
                <button className="w-full px-4 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 flex items-center gap-2" onClick={() => { copyTask(contextMenu.task?.id); setContextMenu(null); }}>
                  <Copy className="w-4 h-4" /> Copy
                </button>
                <button className="w-full px-4 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 flex items-center gap-2" onClick={() => { cutTask(contextMenu.task?.id); setContextMenu(null); }}>
                  <Scissors className="w-4 h-4" /> Cut
                </button>
                <button className="w-full px-4 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 flex items-center gap-2" onClick={() => { pasteTask(); setContextMenu(null); }} disabled={!clipboard}>
                  <Clipboard className="w-4 h-4" /> Paste
                </button>
                <div className="h-px bg-slate-700 my-1" />
                <button className="w-full px-4 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 flex items-center gap-2" onClick={() => { setAttachDocsForTask(contextMenu.task); setContextMenu(null); }} data-testid="ctx-attach-docs">
                  <Paperclip className="w-4 h-4" /> Attach Documents…
                </button>
                <div className="h-px bg-slate-700 my-1" />
                <button className="w-full px-4 py-2 text-sm text-left text-red-400 hover:bg-slate-700 flex items-center gap-2" onClick={() => { deleteTask(contextMenu.task?.id); setContextMenu(null); }}>
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Save Baseline Dialog */}
          <Dialog open={showBaselineDialog} onOpenChange={setShowBaselineDialog}>
            <DialogContent className="bg-slate-800 border-slate-700">
              <DialogHeader>
                <DialogTitle className="text-white">Save Baseline</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-slate-400">Save the current schedule as a baseline to track changes.</p>
                <div>
                  <Label className="text-slate-300">Baseline Name</Label>
                  <Input
                    value={newBaselineName}
                    onChange={(e) => setNewBaselineName(e.target.value)}
                    placeholder="e.g., Original Plan"
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowBaselineDialog(false)}>Cancel</Button>
                <Button onClick={saveBaseline} className="bg-[#1E4E8C]">Save Baseline</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* AI Schedule Generator Panel */}
          <AISchedulePanel
            isOpen={showAIPanel}
            onClose={() => setShowAIPanel(false)}
            projectId={scheduleProjectId}
            projectName={project?.name || 'Project'}
            projectStartDate={project?.start_date?.split('T')[0]}
            existingTaskCount={tasks.length}
            onScheduleGenerated={(newTasks) => {
              setTasks(newTasks);
              toast.success('Schedule updated from AI generation');
            }}
          />

          {/* Delete Entire Schedule Dialog */}
          <Dialog open={showDeleteScheduleDialog} onOpenChange={setShowDeleteScheduleDialog}>
            <DialogContent className="bg-slate-800 border-slate-700" data-testid="delete-schedule-dialog">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Trash className="w-5 h-5 text-red-400" />
                  Delete Entire Macro Schedule
                </DialogTitle>
                <DialogDescription className="text-slate-300">
                  This will permanently delete all <b>{tasks.length.toLocaleString()}</b> tasks, assignments, roll-ups, and baselines for this schedule.
                  The schedule project itself will be kept so you can regenerate it.
                  <span className="block mt-2 text-amber-300 text-xs">This action cannot be undone.</span>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteScheduleDialog(false)} className="border-slate-600" disabled={isDeletingSchedule}>
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    setIsDeletingSchedule(true);
                    try {
                      const r = await api.delete(`/scheduling/projects/${scheduleProjectId}/tasks/all`);
                      toast.success(`Deleted ${r.data?.deleted_tasks ?? 0} tasks — schedule cleared`);
                      setTasks([]);
                      setSelectedRow(null);
                      setSelectedRows(new Set());
                      setShowDeleteScheduleDialog(false);
                      fetchProject();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to delete schedule');
                    } finally {
                      setIsDeletingSchedule(false);
                    }
                  }}
                  disabled={isDeletingSchedule}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-schedule-btn"
                >
                  {isDeletingSchedule ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash className="w-4 h-4 mr-2" />}
                  Delete Entire Schedule
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Cognara — Schedule Impact Narrative dialog */}
          <Dialog open={impactDialogOpen} onOpenChange={setImpactDialogOpen}>
            <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 max-w-3xl max-h-[85vh] flex flex-col p-0" data-testid="impact-dialog">
              <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
                <DialogTitle className="text-slate-900 dark:text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-500 dark:text-violet-400" />
                  Schedule Impact — Cognara analysis
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-6 py-3 space-y-4 min-h-0">
                {/* Headline metrics */}
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-2">
                    <p className="text-[10px] uppercase text-slate-600 dark:text-slate-400">Tasks shifted</p>
                    <p className="text-xl text-slate-900 dark:text-white font-semibold">{impactReport?.impacted?.length ?? 0}</p>
                  </div>
                  <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-2">
                    <p className="text-[10px] uppercase text-slate-600 dark:text-slate-400">Critical-path moves</p>
                    <p className={`text-xl font-semibold ${impactReport?.critical_shifted_count ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{impactReport?.critical_shifted_count ?? 0}</p>
                  </div>
                  <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-2">
                    <p className="text-[10px] uppercase text-slate-600 dark:text-slate-400">Largest slip</p>
                    <p className="text-xl text-amber-600 dark:text-amber-400 font-semibold">{impactReport?.largest_finish_shift_days ? `${impactReport.largest_finish_shift_days > 0 ? '+' : ''}${impactReport.largest_finish_shift_days}d` : '—'}</p>
                  </div>
                  <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-2">
                    <p className="text-[10px] uppercase text-slate-600 dark:text-slate-400">Project finish</p>
                    <p className="text-[11px] text-slate-700 dark:text-slate-200">{impactReport?.before_finish?.slice(0,10) || '—'} → <span className="text-slate-900 dark:text-white">{impactReport?.after_finish?.slice(0,10) || '—'}</span></p>
                  </div>
                </div>

                {/* Narrative paragraph */}
                <div className="rounded-md border border-violet-300 dark:border-violet-500/30 bg-violet-50 dark:bg-violet-500/5 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
                    <span className="text-[11px] uppercase tracking-wide text-violet-700 dark:text-violet-300">Cognara narrative</span>
                  </div>
                  <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed">{impactReport?.narrative}</p>
                </div>

                {/* RAID badge if auto-raised */}
                {impactReport?.raid_item?.raidNumber && (
                  <div className="rounded-md border border-amber-400 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/5 p-3 flex items-center gap-3">
                    <AlertTriangle className="w-4 h-4 text-amber-700 dark:text-amber-400 shrink-0" />
                    <div className="text-xs text-amber-800 dark:text-amber-200 flex-1">
                      Filed to RAID as <strong className="font-mono">{impactReport.raid_item.raidNumber}</strong> — {impactReport.raid_item.priority} priority delay-risk for sponsor visibility. <span className="text-amber-700 dark:text-amber-300">Click the button below to select which additional activities to raise as risks.</span>
                    </div>
                  </div>
                )}

                {/* Impacted tasks table */}
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-600 dark:text-slate-400 mb-2">Impacted activities ({impactReport?.impacted?.length || 0})</p>
                  <div className="border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden">
                    <div className="max-h-72 overflow-y-auto">
                      <table className="w-full text-xs" data-testid="impact-tasks-table">
                        <thead className="bg-slate-100 dark:bg-slate-800/80 sticky top-0">
                          <tr className="text-slate-600 dark:text-slate-400 text-left">
                            <th className="px-3 py-2 font-medium w-24">WBS</th>
                            <th className="px-3 py-2 font-medium">Activity</th>
                            <th className="px-3 py-2 font-medium w-20">Δ Start</th>
                            <th className="px-3 py-2 font-medium w-20">Δ Finish</th>
                            <th className="px-3 py-2 font-medium">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(impactReport?.impacted || []).map((t, idx) => (
                            <tr key={t.id || idx} className={`border-t border-slate-200 dark:border-slate-700 ${t.is_critical ? 'bg-red-50 dark:bg-red-500/5' : ''}`}>
                              <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500 dark:text-slate-400">{t.wbs_code || '—'}</td>
                              <td className="px-3 py-1.5 text-slate-800 dark:text-slate-200">
                                {t.is_critical && <span className="text-red-600 dark:text-red-400 mr-1" title="Critical path">▣</span>}
                                {t.name}
                              </td>
                              <td className={`px-3 py-1.5 font-mono ${t.ds_start_days > 0 ? 'text-amber-700 dark:text-amber-300' : t.ds_start_days < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'}`}>
                                {t.ds_start_days ? `${t.ds_start_days > 0 ? '+' : ''}${t.ds_start_days}d` : '—'}
                              </td>
                              <td className={`px-3 py-1.5 font-mono ${t.ds_finish_days > 0 ? 'text-amber-700 dark:text-amber-300' : t.ds_finish_days < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'}`}>
                                {t.ds_finish_days ? `${t.ds_finish_days > 0 ? '+' : ''}${t.ds_finish_days}d` : '—'}
                              </td>
                              <td className="px-3 py-1.5 text-slate-600 dark:text-slate-400">{t.reason}</td>
                            </tr>
                          ))}
                          {(!impactReport?.impacted || impactReport.impacted.length === 0) && (
                            <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No date movements detected.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="px-6 py-3 border-t border-slate-200 dark:border-slate-700 shrink-0">
                {impactReport?.raid_item?.raidNumber && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Pre-select all critical-path activities by default
                      const preselect = new Set(
                        (impactReport?.impacted || [])
                          .filter(t => t.is_critical || Math.abs(t.ds_finish_days || 0) >= 5)
                          .map(t => t.id)
                      );
                      setSelectedRiskIds(preselect);
                      setRiskPickerOpen(true);
                    }}
                    className="border-amber-500 bg-amber-50 hover:bg-amber-100 text-amber-800 dark:border-amber-500/40 dark:bg-transparent dark:text-amber-300 dark:hover:bg-amber-500/10"
                    data-testid="impact-open-raid"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                    Add More Risks to RAID ({impactReport.raid_item.raidNumber})
                  </Button>
                )}
                <Button onClick={() => setImpactDialogOpen(false)} className="bg-[#1E4E8C] hover:bg-[#143A6B] text-white" data-testid="impact-dismiss">Dismiss</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Risk Selection Picker Dialog — pick which impacted activities to raise as risks */}
          <Dialog open={riskPickerOpen} onOpenChange={setRiskPickerOpen}>
            <DialogContent className="max-w-3xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
              <DialogHeader>
                <DialogTitle className="text-slate-800 dark:text-white flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" /> Select Risks to Add to RAID
                </DialogTitle>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                  Pre-selected: critical-path activities + any shifting ≥ 5 days. Toggle each row to include/exclude.
                </p>
              </DialogHeader>

              <div className="flex items-center justify-between text-xs mb-2">
                <div className="text-slate-600 dark:text-slate-300">
                  <span className="font-semibold text-amber-600 dark:text-amber-400">{selectedRiskIds.size}</span> of {impactReport?.impacted?.length || 0} selected
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setSelectedRiskIds(new Set((impactReport?.impacted || []).map(t => t.id)))} data-testid="risk-select-all">Select All</Button>
                  <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setSelectedRiskIds(new Set())} data-testid="risk-select-none">Clear</Button>
                </div>
              </div>

              <div className="border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden max-h-[55vh] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 dark:bg-slate-800/60 sticky top-0">
                    <tr className="text-slate-600 dark:text-slate-400 text-left">
                      <th className="p-2 w-8"></th>
                      <th className="p-2 w-20 font-medium">WBS</th>
                      <th className="p-2 font-medium">Activity</th>
                      <th className="p-2 w-16 font-medium">Δ Finish</th>
                      <th className="p-2 w-24 font-medium">Priority</th>
                      <th className="p-2 w-24 font-medium text-center">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(impactReport?.impacted || []).map((t) => {
                      const isSel = selectedRiskIds.has(t.id);
                      const detail = riskDetails[t.id] || defaultRiskDetail(t);
                      const priority = detail.priority;
                      const priorityColor = priority === 'HIGH' ? 'red' : priority === 'MEDIUM' ? 'amber' : 'sky';
                      const isEditing = detailEditingId === t.id;
                      const hasCustom = !!riskDetails[t.id];
                      return (
                        <React.Fragment key={t.id}>
                          <tr className={`border-t border-slate-200 dark:border-slate-700 ${t.is_critical ? 'bg-red-50 dark:bg-red-500/5' : ''} hover:bg-slate-50 dark:hover:bg-slate-800/40`}>
                            <td className="p-2 text-center">
                              <input type="checkbox" checked={isSel}
                                     onChange={() => setSelectedRiskIds(prev => { const n = new Set(prev); if (n.has(t.id)) n.delete(t.id); else n.add(t.id); return n; })}
                                     data-testid={`risk-check-${t.id}`} />
                            </td>
                            <td className="p-2 font-mono text-[10px] text-slate-500 dark:text-slate-400">{t.wbs_code || '—'}</td>
                            <td className="p-2 text-slate-800 dark:text-slate-200">{t.name}</td>
                            <td className={`p-2 font-mono ${t.ds_finish_days > 0 ? 'text-amber-700 dark:text-amber-300' : t.ds_finish_days < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'}`}>
                              {t.ds_finish_days ? `${t.ds_finish_days > 0 ? '+' : ''}${t.ds_finish_days}d` : '—'}
                            </td>
                            <td className="p-2">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold bg-${priorityColor}-100 dark:bg-${priorityColor}-500/20 text-${priorityColor}-700 dark:text-${priorityColor}-300`}>{priority}</span>
                            </td>
                            <td className="p-2 text-center">
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]"
                                onClick={() => {
                                  if (!hasCustom) {
                                    setRiskDetails(prev => ({ ...prev, [t.id]: defaultRiskDetail(t) }));
                                  }
                                  setDetailEditingId(isEditing ? null : t.id);
                                }}
                                data-testid={`risk-detail-toggle-${t.id}`}>
                                {isEditing ? 'Close' : hasCustom ? 'Edit ✓' : 'Edit'}
                              </Button>
                            </td>
                          </tr>
                          {isEditing && (
                            <tr className="bg-slate-50 dark:bg-slate-800/40">
                              <td colSpan={6} className="p-3 border-t border-slate-200 dark:border-slate-700">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                                  <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Owner</label>
                                    <Input value={detail.owner} onChange={(e) => setRiskDetails(prev => ({ ...prev, [t.id]: { ...detail, owner: e.target.value } }))} className="h-7 text-xs" data-testid={`detail-owner-${t.id}`} />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Domain Category</label>
                                    <select value={detail.domainCategory} onChange={(e) => setRiskDetails(prev => ({ ...prev, [t.id]: { ...detail, domainCategory: e.target.value } }))} className="w-full h-7 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2">
                                      {['PROGRAMME','SAFETY','QUALITY','COST','ENVIRONMENTAL','TECHNICAL','COMMERCIAL','REGULATORY','RESOURCES','SUPPLY_CHAIN'].map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Priority</label>
                                    <select value={detail.priority} onChange={(e) => setRiskDetails(prev => ({ ...prev, [t.id]: { ...detail, priority: e.target.value } }))} className="w-full h-7 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2">
                                      {['HIGH','MEDIUM','LOW'].map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Probability (1-5)</label>
                                    <Input type="number" min={1} max={5} value={detail.probabilityScore} onChange={(e) => setRiskDetails(prev => ({ ...prev, [t.id]: { ...detail, probabilityScore: parseInt(e.target.value) || 1 } }))} className="h-7 text-xs" />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Impact (1-5)</label>
                                    <Input type="number" min={1} max={5} value={detail.impactScore} onChange={(e) => setRiskDetails(prev => ({ ...prev, [t.id]: { ...detail, impactScore: parseInt(e.target.value) || 1 } }))} className="h-7 text-xs" />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Response Strategy</label>
                                    <select value={detail.responseStrategy} onChange={(e) => setRiskDetails(prev => ({ ...prev, [t.id]: { ...detail, responseStrategy: e.target.value } }))} className="w-full h-7 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2">
                                      {['MITIGATE','AVOID','TRANSFER','ACCEPT'].map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                  </div>
                                  <div className="md:col-span-3">
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Description</label>
                                    <textarea value={detail.description} onChange={(e) => setRiskDetails(prev => ({ ...prev, [t.id]: { ...detail, description: e.target.value } }))} rows={2} className="w-full text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1" />
                                  </div>
                                  <div className="md:col-span-3">
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Mitigation / Action Plan</label>
                                    <textarea value={detail.mitigation} onChange={(e) => setRiskDetails(prev => ({ ...prev, [t.id]: { ...detail, mitigation: e.target.value } }))} rows={2} placeholder="How will this risk be managed?" className="w-full text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1" />
                                  </div>
                                </div>
                                <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
                                  <span>Risk Score = <span className="font-bold text-slate-800 dark:text-slate-100">{detail.probabilityScore * detail.impactScore}</span> ({detail.probabilityScore * detail.impactScore >= 16 ? 'HIGH' : detail.probabilityScore * detail.impactScore >= 9 ? 'MEDIUM' : 'LOW'} rating)</span>
                                  <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setDetailEditingId(null)}>Done</Button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setRiskPickerOpen(false)}>Cancel</Button>
                <Button
                  disabled={selectedRiskIds.size === 0 || creatingRisks}
                  onClick={async () => {
                    setCreatingRisks(true);
                    const selected = (impactReport?.impacted || []).filter(t => selectedRiskIds.has(t.id));
                    try {
                      const payload = {
                        project_id: parentProjectId,
                        parent_raid_number: impactReport?.raid_item?.raidNumber,
                        risks: selected.map(t => {
                          const custom = riskDetails[t.id] || defaultRiskDetail(t);
                          return {
                            activity_id: t.id,
                            wbs_code:    t.wbs_code || '',
                            activity_name: t.name,
                            delta_finish_days: t.ds_finish_days || 0,
                            is_critical: !!t.is_critical,
                            priority: custom.priority,
                            reason: t.reason,
                            // Detailed RAID form fields
                            owner:             custom.owner,
                            domainCategory:    custom.domainCategory,
                            description:       custom.description,
                            mitigation:        custom.mitigation,
                            probabilityScore:  custom.probabilityScore,
                            impactScore:       custom.impactScore,
                            responseStrategy:  custom.responseStrategy,
                          };
                        }),
                      };
                      const r = await api.post(`/scheduling/projects/${scheduleProjectId}/raise-risks-bulk`, payload);
                      toast.success(`Added ${r.data.created} risk(s) to RAID`);
                      setRiskPickerOpen(false);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Failed to add risks');
                    } finally {
                      setCreatingRisks(false);
                    }
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="risk-create-btn"
                >
                  {creatingRisks ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1" />}
                  Add {selectedRiskIds.size} Risk{selectedRiskIds.size === 1 ? '' : 's'} to RAID
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Task Attachments — attach DMS documents to any Macro-Schedule activity */}
          <TaskAttachmentsDialog
            open={!!attachDocsForTask}
            onClose={() => setAttachDocsForTask(null)}
            projectId={scheduleProjectId}
            dmsProjectId={parentProjectId || scheduleProjectId}
            task={attachDocsForTask ? { id: attachDocsForTask.id, name: attachDocsForTask.name } : null}
            onChanged={() => {
              // refresh attachment counts
              api.get(`/scheduling/projects/${scheduleProjectId}/attachments/summary`).then(r => {
                setTaskAttachmentCounts(r.data?.by_task || {});
              }).catch(() => {});
            }}
          />

          {/* Bulk Attach — attach a document set to multiple activities at once */}
          <DocumentPickerDialog
            open={showBulkAttachPicker}
            projectId={parentProjectId || scheduleProjectId}
            onClose={() => { setShowBulkAttachPicker(false); setBulkAttachTaskIds(null); }}
            onPick={async (docs) => {
              if (!bulkAttachTaskIds || bulkAttachTaskIds.length === 0 || !docs?.length) {
                setShowBulkAttachPicker(false);
                return;
              }
              setBulkAttaching(true);
              const docIds = docs.map((d) => d.id);
              let ok = 0, fail = 0;
              // Attach in parallel across tasks with limited concurrency to avoid overwhelming the API
              const chunkSize = 8;
              for (let i = 0; i < bulkAttachTaskIds.length; i += chunkSize) {
                const chunk = bulkAttachTaskIds.slice(i, i + chunkSize);
                const results = await Promise.allSettled(
                  chunk.map((tid) => api.post(`/scheduling/projects/${scheduleProjectId}/tasks/${tid}/attachments`, { doc_ids: docIds }))
                );
                results.forEach((r) => { r.status === 'fulfilled' ? ok++ : fail++; });
              }
              setBulkAttaching(false);
              setShowBulkAttachPicker(false);
              setBulkAttachTaskIds(null);
              if (fail === 0) {
                toast.success(`Attached ${docs.length} document(s) to ${ok} activities`);
              } else {
                toast.error(`Attached to ${ok} activities, ${fail} failed`);
              }
              // Refresh counts
              api.get(`/scheduling/projects/${scheduleProjectId}/attachments/summary`).then((r) => {
                setTaskAttachmentCounts(r.data?.by_task || {});
              }).catch(() => {});
            }}
            title={bulkAttachTaskIds?.length
              ? `Bulk attach — select documents to attach to ${bulkAttachTaskIds.length} activities${bulkAttaching ? ' (attaching…)' : ''}`
              : 'Bulk attach'}
          />
        </div>
      </TooltipProvider>
    </DashboardLayout>
  );
}

// ============================================
// DATE EDIT CELL COMPONENT
// ============================================
function DateEditCell({ value, onChange, onSave, onCancel }) {
  const [open, setOpen] = useState(true);
  const [inputValue, setInputValue] = useState(value || '');
  
  const parseDate = (str) => {
    if (!str) return null;
    try {
      const date = new Date(str);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };
  
  const handleSelect = (selectedDate) => {
    if (selectedDate) {
      const formatted = format(selectedDate, 'yyyy-MM-dd');
      setInputValue(formatted);
      onChange(formatted);
      onSave();
    }
    setOpen(false);
  };
  
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    onChange(e.target.value);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSave();
    }
    if (e.key === 'Escape') {
      onCancel();
    }
    e.stopPropagation();
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="w-full h-full px-2 py-1.5 pr-7 bg-slate-900 border-none outline-none text-white text-sm"
            autoFocus
            placeholder="YYYY-MM-DD"
          />
          <Calendar 
            className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 cursor-pointer hover:text-[#0E7C6B]"
            onClick={() => setOpen(true)}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700 z-50" align="start">
        <CalendarPicker
          mode="single"
          selected={parseDate(inputValue)}
          onSelect={handleSelect}
          initialFocus
          className="bg-slate-900"
        />
      </PopoverContent>
    </Popover>
  );
}

// ============================================
// WBS / TASKS TAB COMPONENT - ENHANCED
// ============================================
function WBSTasksTab({
  project, tasks, visibleTasks, resources, calendars, columns,
  focusedRow, focusedCol, selectedRow, selectedRows, editingCell, editValue,
  expandedTasks, clipboard, inputRef, gridRef, draggedTask, dropTarget,
  setFocusedRow, setFocusedCol, setSelectedRow, setSelectedRows, setExpandedTasks, setEditValue,
  startEditing, saveEdit, cancelEdit, getCellValue, handleContextMenu,
  addTask, deleteTask, indentTask, outdentTask, copyTask, cutTask, pasteTask,
  handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
  createBaselineFromToolbar, autoDistributeWeightage, fetchProject, scheduleProjectId,
  toggleMilestone, updateDurationWithUnit, recalculateSchedule, isRecalculating,
  projectDurationUnit, setProjectDurationUnit, updateTask, setTasks
}) {
  // Permission lookups (component-local scope — outer-scope vars from
  // SchedulingDashboardEnhanced are not visible inside this sub-component).
  const { can: canPerm } = usePermissions();
  const canSchedCreate = canPerm('MACRO_SCHEDULE', 'create');
  const canSchedEdit = canPerm('MACRO_SCHEDULE', 'edit');
  const canSchedDelete = canPerm('MACRO_SCHEDULE', 'delete');
  const canSchedConfigure = canPerm('MACRO_SCHEDULE', 'configure');
  // Dialogs state
  const [showPredecessorDialog, setShowPredecessorDialog] = useState(false);
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  const [showCalendarSelect, setShowCalendarSelect] = useState(false);
  const [showConstraintDialog, setShowConstraintDialog] = useState(false);
  const [dialogTask, setDialogTask] = useState(null);
  const [selectedPredecessors, setSelectedPredecessors] = useState([]); // Array of {predecessor_id, type, lag, lag_unit}
  const [selectedResources, setSelectedResources] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState('');
  const [selectedConstraint, setSelectedConstraint] = useState({ type: 'as_soon_as_possible', date: '' });
  const [weightageValidation, setWeightageValidation] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Handle refresh schedule - recalculates CPM and updates dates
  const handleRefreshSchedule = useCallback(async () => {
    if (!scheduleProjectId) {
      toast.error('No project selected');
      return;
    }
    
    setIsRefreshing(true);
    try {
      // Call the recalculate API
      const token = localStorage.getItem('ciq_token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(
        `${baseUrl}/api/scheduling/projects/${scheduleProjectId}/schedule/recalculate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: '{}'
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP Error ${response.status}`);
      }
      
      const result = await response.json();
      
      // Show success message
      toast.success(
        `Schedule refreshed: ${result.updatedTasks || 0} tasks updated, ${result.criticalPathCount || 0} on critical path`,
        { duration: 4000 }
      );
      
      // Fetch updated tasks
      const tasksRes = await fetch(
        `${baseUrl}/api/scheduling/projects/${scheduleProjectId}/tasks`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        const uniqueTasks = tasksData.reduce((acc, task) => {
          if (!acc.find(t => t.id === task.id)) {
            acc.push(task);
          }
          return acc;
        }, []);
        setTasks(uniqueTasks);
      }
    } catch (err) {
      console.log('Refresh error:', String(err));
      toast.error(err?.message || 'Failed to refresh schedule');
    } finally {
      setIsRefreshing(false);
    }
  }, [scheduleProjectId, setTasks]);

  // Handle keyboard events for the grid
  const handleGridKeyDown = (e) => {
    const currentTasks = visibleTasks;
    const task = currentTasks[focusedRow];
    
    // Prevent default browser behavior for navigation keys
    if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Tab', 'PageDown', 'PageUp', 'Home', 'End'].includes(e.key)) {
      e.preventDefault();
    }
    
    // Handle Cmd/Ctrl shortcuts
    if (e.metaKey || e.ctrlKey) {
      if (e.key === 'c') {
        e.preventDefault();
        // Copy cell value - handle special columns
        const col = columns[focusedCol];
        if (task && col) {
          let copyData = {};
          
          // Handle different column types
          if (col.type === 'calendar' || col.id === 'calendar_id') {
            // Copy calendar with both ID and name for validation on paste
            copyData = {
              type: 'calendar',
              calendar_id: task.calendar_id,
              display: getCellValue(task, col.id)
            };
          } else if (col.type === 'resource' || col.id === 'resources') {
            // Copy resource assignments with full structure
            copyData = {
              type: 'resource',
              resource_assignments: task.resource_assignments || [],
              display: getCellValue(task, col.id)
            };
          } else {
            // Standard copy
            copyData = {
              type: 'standard',
              field: col.id,
              value: task[col.id],
              display: getCellValue(task, col.id)
            };
          }
          
          try {
            // Store structured data as JSON
            const clipboardText = JSON.stringify(copyData);
            navigator.clipboard.writeText(clipboardText);
            toast.success(`Copied: ${copyData.display || '(empty)'}`);
          } catch (err) {
            // Fallback to plain text
            const value = getCellValue(task, col.id);
            navigator.clipboard.writeText(String(value || ''));
            toast.success(`Copied: ${value || '(empty)'}`);
          }
        }
        return;
      }
      if (e.key === 'v') {
        e.preventDefault();
        // Paste - handle structured data
        navigator.clipboard.readText().then(text => {
          const col = columns[focusedCol];
          if (!task || !col?.editable || !text) return;
          
          // Try to parse as structured data
          let pasteData = null;
          try {
            pasteData = JSON.parse(text);
          } catch {
            // Plain text paste
            pasteData = { type: 'standard', value: text };
          }
          
          // Apply to selected rows or current row
          const targetIds = selectedRows.size > 0 ? Array.from(selectedRows) : [task.id];
          
          if (pasteData.type === 'calendar' && (col.type === 'calendar' || col.id === 'calendar_id')) {
            // Validate calendar exists
            const calendarExists = calendars.some(c => c.id === pasteData.calendar_id);
            if (calendarExists || !pasteData.calendar_id) {
              targetIds.forEach(id => {
                updateTask(id, { calendar_id: pasteData.calendar_id }, true);
              });
              toast.success(`Pasted calendar to ${targetIds.length} cell(s)`);
            } else {
              toast.error('Calendar not found in project');
            }
          } else if (pasteData.type === 'resource' && (col.type === 'resource' || col.id === 'resources')) {
            // Validate resources exist
            const validResources = (pasteData.resource_assignments || []).filter(aId => 
              resources.some(r => r.id === aId)
            );
            targetIds.forEach(id => {
              updateTask(id, { resource_assignments: validResources }, true);
            });
            toast.success(`Pasted resources to ${targetIds.length} cell(s)`);
          } else {
            // Standard paste
            const value = pasteData.value !== undefined ? pasteData.value : text;
            targetIds.forEach(id => {
              updateTask(id, { [col.id]: value }, true);
            });
            toast.success(`Pasted to ${targetIds.length} cell(s)`);
          }
        }).catch(() => {
          toast.error('Unable to access clipboard');
        });
        return;
      }
    }
    
    switch (e.key) {
      case 'ArrowDown':
        if (focusedRow < currentTasks.length - 1) {
          const newRow = focusedRow + 1;
          const newTaskId = currentTasks[newRow]?.id;
          
          if (e.shiftKey) {
            // Shift+Down for multi-select
            setSelectedRows(prev => {
              const newSet = new Set(prev);
              if (task?.id) newSet.add(task.id);
              if (newTaskId) newSet.add(newTaskId);
              return newSet;
            });
          } else {
            setSelectedRows(new Set());
          }
          
          setFocusedRow(newRow);
          setSelectedRow(newTaskId);
          
          // Auto-scroll to keep the focused row visible
          scrollRowIntoView(newRow);
        }
        break;
        
      case 'ArrowUp':
        if (focusedRow > 0) {
          const newRow = focusedRow - 1;
          const newTaskId = currentTasks[newRow]?.id;
          
          if (e.shiftKey) {
            // Shift+Up for multi-select
            setSelectedRows(prev => {
              const newSet = new Set(prev);
              if (task?.id) newSet.add(task.id);
              if (newTaskId) newSet.add(newTaskId);
              return newSet;
            });
          } else {
            setSelectedRows(new Set());
          }
          
          setFocusedRow(newRow);
          setSelectedRow(newTaskId);
          
          // Auto-scroll to keep the focused row visible
          scrollRowIntoView(newRow);
        }
        break;
        
      case 'ArrowRight':
        const newColRight = Math.min(focusedCol + 1, columns.length - 1);
        setFocusedCol(newColRight);
        // Auto-scroll horizontally
        scrollColumnIntoView(newColRight);
        break;
        
      case 'ArrowLeft':
        const newColLeft = Math.max(focusedCol - 1, 0);
        setFocusedCol(newColLeft);
        // Auto-scroll horizontally - if at column 0, scroll all the way left
        if (newColLeft === 0 && gridRef.current) {
          gridRef.current.scrollLeft = 0;
        } else {
          scrollColumnIntoView(newColLeft);
        }
        break;
      
      case 'PageDown':
        // Jump 10 rows or to end
        const pageDownRow = Math.min(focusedRow + 10, currentTasks.length - 1);
        setFocusedRow(pageDownRow);
        setSelectedRow(currentTasks[pageDownRow]?.id);
        scrollRowIntoView(pageDownRow);
        break;
        
      case 'PageUp':
        // Jump 10 rows up or to start
        const pageUpRow = Math.max(focusedRow - 10, 0);
        setFocusedRow(pageUpRow);
        setSelectedRow(currentTasks[pageUpRow]?.id);
        scrollRowIntoView(pageUpRow);
        break;
      
      case 'Home':
        if (e.ctrlKey || e.metaKey) {
          // Ctrl+Home - go to first cell
          setFocusedRow(0);
          setFocusedCol(0);
          setSelectedRow(currentTasks[0]?.id);
          scrollRowIntoView(0);
          // Scroll all the way left to show WBS/Task Name
          if (gridRef.current) gridRef.current.scrollLeft = 0;
        } else {
          // Home - go to first column and scroll left
          setFocusedCol(0);
          if (gridRef.current) gridRef.current.scrollLeft = 0;
        }
        break;
        
      case 'End':
        if (e.ctrlKey || e.metaKey) {
          // Ctrl+End - go to last cell
          const lastRow = currentTasks.length - 1;
          const lastCol = columns.length - 1;
          setFocusedRow(lastRow);
          setFocusedCol(lastCol);
          setSelectedRow(currentTasks[lastRow]?.id);
          scrollRowIntoView(lastRow);
          scrollColumnIntoView(lastCol);
        } else {
          // End - go to last column
          setFocusedCol(columns.length - 1);
          scrollColumnIntoView(columns.length - 1);
        }
        break;
        
      case 'Enter':
      case 'F2':
        // Start editing current cell
        const col = columns[focusedCol];
        if (col?.editable && task) {
          startEditing(task.id, col.id, getCellValue(task, col.id));
        }
        break;
        
      case 'Delete':
        if (task) deleteTask(task.id);
        break;
        
      case 'Escape':
        setSelectedRows(new Set());
        break;
        
      default:
        break;
    }
  };
  
  // Scroll helper functions for grid navigation
  // Uses scrollTop/scrollLeft for precise control - prevents unwanted cross-axis scrolling
  const scrollRowIntoView = (rowIndex) => {
    if (!gridRef.current) return;
    const container = gridRef.current;
    const rows = container.querySelectorAll('tbody tr');
    const row = rows[rowIndex];
    if (!row) return;
    
    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    const containerTop = container.scrollTop;
    const containerBottom = containerTop + container.clientHeight;
    const headerHeight = container.querySelector('thead')?.offsetHeight || 40;
    
    // Only scroll vertically - don't affect horizontal scroll
    if (rowTop < containerTop + headerHeight) {
      // Row is above visible area - scroll up
      container.scrollTop = rowTop - headerHeight;
    } else if (rowBottom > containerBottom) {
      // Row is below visible area - scroll down
      container.scrollTop = rowBottom - container.clientHeight + 10;
    }
  };
  
  const scrollColumnIntoView = (colIndex) => {
    if (!gridRef.current) return;
    const container = gridRef.current;
    const headerCells = container.querySelectorAll('thead th');
    // Add 2 to account for checkbox and row number columns
    const cell = headerCells[colIndex + 2];
    if (!cell) return;
    
    const cellLeft = cell.offsetLeft;
    const cellRight = cellLeft + cell.offsetWidth;
    const containerLeft = container.scrollLeft;
    const containerRight = containerLeft + container.clientWidth;
    
    // Calculate the width of frozen columns (checkbox + row number + WBS + Task Name)
    // These first columns should always be visible, so we need to account for them
    const frozenWidth = 0; // We'll scroll all the way left if needed
    
    // Only scroll horizontally - don't affect vertical scroll
    if (cellLeft < containerLeft + frozenWidth) {
      // Cell is to the left of visible area - scroll left
      container.scrollLeft = Math.max(0, cellLeft - frozenWidth - 10);
    } else if (cellRight > containerRight) {
      // Cell is to the right of visible area - scroll right
      container.scrollLeft = cellRight - container.clientWidth + 10;
    }
  };

  // Check weightage validation for a parent task
  const checkWeightageValidation = (parentId) => {
    if (!parentId) return null;
    const children = tasks.filter(t => t.parent_id === parentId && !t.is_summary);
    if (children.length === 0) return null;
    
    const totalWeightage = children.reduce((sum, child) => {
      return sum + (child.weightage || child.weight || 0);
    }, 0);
    
    if (totalWeightage > 100) {
      return { type: 'error', message: `Total weightage (${totalWeightage.toFixed(1)}%) exceeds 100%` };
    } else if (totalWeightage < 100) {
      return { type: 'warning', message: `Total weightage (${totalWeightage.toFixed(1)}%) is less than 100%` };
    }
    return { type: 'valid', message: 'Weightage equals 100%' };
  };

  // Open predecessor dialog with full dependency info
  const openPredecessorDialog = (task) => {
    setDialogTask(task);
    // Map existing dependencies to include type and lag
    const deps = (task.dependencies || []).map(d => ({
      predecessor_id: d.predecessor_id,
      type: d.type || 'finish_to_start',
      lag: d.lag || 0,
      lag_unit: d.lag_type || task.duration_unit || 'hours'
    }));
    setSelectedPredecessors(deps);
    setShowPredecessorDialog(true);
  };

  // Open resource dialog
  const openResourceDialog = (task) => {
    setDialogTask(task);
    setSelectedResources(task.resource_assignments || []);
    setShowResourceDialog(true);
  };

  // Open calendar select
  const openCalendarSelect = (task) => {
    setDialogTask(task);
    setSelectedCalendar(task.calendar_id || '');
    setShowCalendarSelect(true);
  };

  // Open constraint dialog
  const openConstraintDialog = (task) => {
    setDialogTask(task);
    setSelectedConstraint({
      type: task.constraint_type || 'as_soon_as_possible',
      date: task.constraint_date || ''
    });
    setShowConstraintDialog(true);
  };

  // Save predecessor selection with type and lag
  const savePredecessors = async () => {
    if (!dialogTask) return;
    try {
      // Format dependencies with type and lag
      const dependencies = selectedPredecessors.map(dep => ({
        predecessor_id: dep.predecessor_id,
        type: dep.type || 'finish_to_start',
        lag: parseInt(dep.lag) || 0,
        lag_type: dep.lag_unit || 'hours'
      }));
      
      const response = await api.put(`/scheduling/tasks/${dialogTask.id}`, { dependencies });
      toast.success('Predecessors updated - recalculating schedule...');
      setShowPredecessorDialog(false);
      
      // Trigger full recalculation since dependencies changed
      await recalculateSchedule(dialogTask.id);
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to update predecessors');
      toast.error(errorMsg);
    }
  };
  
  // Toggle predecessor selection
  const togglePredecessor = (taskId, checked) => {
    if (checked) {
      setSelectedPredecessors([...selectedPredecessors, {
        predecessor_id: taskId,
        type: 'finish_to_start',
        lag: 0,
        lag_unit: dialogTask?.duration_unit || 'hours'
      }]);
    } else {
      setSelectedPredecessors(selectedPredecessors.filter(p => p.predecessor_id !== taskId));
    }
  };
  
  // Update predecessor dependency type
  const updatePredecessorType = (predId, newType) => {
    setSelectedPredecessors(selectedPredecessors.map(p => 
      p.predecessor_id === predId ? { ...p, type: newType } : p
    ));
  };
  
  // Update predecessor lag
  const updatePredecessorLag = (predId, lag, unit) => {
    setSelectedPredecessors(selectedPredecessors.map(p => 
      p.predecessor_id === predId ? { ...p, lag: lag, lag_unit: unit || p.lag_unit } : p
    ));
  };

  // Save resource selection
  const saveResources = async () => {
    if (!dialogTask) return;
    try {
      // Optimistic update
      setTasks(prev => prev.map(t => 
        t.id === dialogTask.id ? { ...t, resource_assignments: selectedResources } : t
      ));
      
      await api.put(`/scheduling/tasks/${dialogTask.id}`, { resource_assignments: selectedResources });
      toast.success('Resources updated');
      setShowResourceDialog(false);
    } catch (err) {
      toast.error('Failed to update resources');
      // Revert on error - fetch tasks only
      const tasksRes = await api.get(`/scheduling/projects/${scheduleProjectId}/tasks`);
      if (tasksRes.data) {
        setTasks(tasksRes.data);
      }
    }
  };

  // Save calendar selection (including un-assignment)
  const saveCalendar = async () => {
    if (!dialogTask) return;
    try {
      // Optimistic update - use null for empty string to clear calendar
      const calendarValue = selectedCalendar === '' ? null : selectedCalendar;
      
      setTasks(prev => prev.map(t => 
        t.id === dialogTask.id ? { ...t, calendar_id: calendarValue } : t
      ));
      
      await api.put(`/scheduling/tasks/${dialogTask.id}`, { calendar_id: calendarValue });
      toast.success(calendarValue ? 'Calendar assigned - recalculating...' : 'Calendar removed - recalculating...');
      setShowCalendarSelect(false);
      
      // Calendar change affects dates, trigger recalculation
      recalculateSchedule(dialogTask.id);
    } catch (err) {
      toast.error('Failed to update calendar');
      // Revert on error - fetch tasks only
      const tasksRes = await api.get(`/scheduling/projects/${scheduleProjectId}/tasks`);
      if (tasksRes.data) {
        setTasks(tasksRes.data);
      }
    }
  };

  // Save constraint
  const saveConstraint = async () => {
    if (!dialogTask) return;
    try {
      // Optimistic update
      setTasks(prev => prev.map(t => 
        t.id === dialogTask.id ? { 
          ...t, 
          constraint_type: selectedConstraint.type,
          constraint_date: selectedConstraint.date || null 
        } : t
      ));
      
      await api.put(`/scheduling/tasks/${dialogTask.id}`, {
        constraint_type: selectedConstraint.type,
        constraint_date: selectedConstraint.date || null
      });
      toast.success('Constraint updated - recalculating...');
      setShowConstraintDialog(false);
      
      // Constraint change affects dates, trigger recalculation
      recalculateSchedule(dialogTask.id);
    } catch (err) {
      toast.error('Failed to update constraint');
      // Revert on error - fetch tasks only
      const tasksRes = await api.get(`/scheduling/projects/${scheduleProjectId}/tasks`);
      if (tasksRes.data) {
        setTasks(tasksRes.data);
      }
    }
  };

  // Constraint type requires date
  const constraintRequiresDate = (type) => {
    return ['must_start_on', 'must_finish_on', 'start_no_earlier_than', 'start_no_later_than', 'finish_no_earlier_than', 'finish_no_later_than'].includes(type);
  };
  
  // Handle project-level duration unit change
  const handleDurationUnitChange = async (newUnit) => {
    try {
      const hoursPerDay = 8;
      
      // Update all tasks with converted durations
      const updatedTasks = tasks.map(task => {
        if (task.is_summary) return task; // Don't update summary tasks
        
        let newDuration = task.duration || 0;
        const oldUnit = task.duration_unit || 'hours';
        
        // Convert duration to new unit
        if (oldUnit === 'hours' && newUnit === 'days') {
          newDuration = task.duration / hoursPerDay;
        } else if (oldUnit === 'days' && newUnit === 'hours') {
          newDuration = task.duration * hoursPerDay;
        }
        
        return { ...task, duration: newDuration, duration_unit: newUnit };
      });
      
      // Optimistic update
      setTasks(updatedTasks);
      
      // Update project setting on server
      await api.put(`/scheduling/projects/${scheduleProjectId}`, { duration_unit: newUnit });
      
      // Batch update all task durations (convert them)
      for (const task of updatedTasks.filter(t => !t.is_summary)) {
        await api.put(`/scheduling/tasks/${task.id}`, { 
          duration: task.duration, 
          duration_unit: newUnit 
        });
      }
      
      toast.success(`Duration unit changed to ${newUnit}. All durations converted.`);
      
      // Trigger recalculation
      recalculateSchedule(null);
    } catch (err) {
      toast.error('Failed to change duration unit');
      console.error(err);
    }
  };

  // Handle cell click for special types
  const handleCellClick = (task, col, rowIdx, colIdx) => {
    setFocusedRow(rowIdx);
    setFocusedCol(colIdx);
    setSelectedRow(task.id);
    
    // Summary tasks cannot have dependencies, so don't open dialogs
    if (task.is_summary && ['predecessor', 'resource', 'calendar'].includes(col.type)) {
      toast.info('Summary tasks have roll-up values from children');
      return;
    }
    
    if (col.type === 'predecessor') {
      openPredecessorDialog(task);
    } else if (col.type === 'resource') {
      openResourceDialog(task);
    } else if (col.type === 'calendar') {
      openCalendarSelect(task);
    } else if (col.type === 'constraint') {
      openConstraintDialog(task);
    } else if (col.type === 'date' && (col.id === 'actual_start' || col.id === 'actual_finish')) {
      // For actual dates, allow single-click editing
      const isEditable = col.editable && isFieldEditable(task, col.id);
      if (isEditable && !task.is_summary) {
        startEditing(task.id, col.id, getCellValue(task, col.id));
      }
    }
  };

  return (
    <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl overflow-hidden">
      {/* Title Section */}
      <div className="p-4 border-b border-slate-700/30">
        <h2 className="text-lg font-semibold text-white">Work Breakdown Structure</h2>
        <p className="text-sm text-slate-500">
          Double-click to edit. Drag rows to reorder. Alt+D to indent, Alt+A to outdent. 
          <span className="text-slate-600 ml-2">• Summary tasks have auto-calculated values (grayed out)</span>
        </p>
      </div>
      
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-slate-700/30 bg-[#0a0f1e] flex-wrap">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" onClick={() => addTask(visibleTasks[visibleTasks.length - 1]?.id)} className="h-8 w-8 p-0 hover:bg-slate-700" data-testid="toolbar-add" disabled={!canSchedCreate}>
              <Plus className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Add Task (Insert)</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" onClick={() => selectedRow && deleteTask(selectedRow)} disabled={!selectedRow || !canSchedDelete} className="h-8 w-8 p-0 hover:bg-slate-700" data-testid="toolbar-delete">
              <Trash2 className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Delete (Del)</TooltipContent>
        </Tooltip>
        <div className="w-px h-5 bg-slate-700 mx-1" />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" onClick={() => selectedRow && indentTask(selectedRow)} disabled={!selectedRow} className="h-8 w-8 p-0 hover:bg-slate-700">
              <Indent className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Indent (Alt+D)</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" onClick={() => selectedRow && outdentTask(selectedRow)} disabled={!selectedRow} className="h-8 w-8 p-0 hover:bg-slate-700">
              <Outdent className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Outdent (Alt+A)</TooltipContent>
        </Tooltip>
        <div className="w-px h-5 bg-slate-700 mx-1" />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" onClick={() => selectedRow && copyTask(selectedRow)} disabled={!selectedRow} className="h-8 w-8 p-0 hover:bg-slate-700">
              <Copy className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy (Ctrl+C)</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" onClick={() => selectedRow && cutTask(selectedRow)} disabled={!selectedRow} className="h-8 w-8 p-0 hover:bg-slate-700">
              <Scissors className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Cut (Ctrl+X)</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" onClick={pasteTask} disabled={!clipboard} className="h-8 w-8 p-0 hover:bg-slate-700">
              <Clipboard className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Paste (Ctrl+V)</TooltipContent>
        </Tooltip>
        <div className="w-px h-5 bg-slate-700 mx-1" />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" onClick={createBaselineFromToolbar} className="h-8 px-2 hover:bg-slate-700 gap-1">
              <Flag className="w-4 h-4" />
              <span className="text-xs">Baseline</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Create Baseline</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" onClick={() => autoDistributeWeightage(null)} className="h-8 px-2 hover:bg-slate-700 gap-1">
              <Percent className="w-4 h-4" />
              <span className="text-xs">Auto Wgt</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Auto-distribute Weightage</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleRefreshSchedule} 
              disabled={isRefreshing}
              className="h-8 px-2 hover:bg-slate-700 gap-1"
              data-testid="toolbar-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="text-xs">Refresh</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Refresh & Recalculate Schedule (CPM)</TooltipContent>
        </Tooltip>
        <div className="w-px h-5 bg-slate-700 mx-1" />
        
        {/* Duration Unit Display - Days only */}
        <div className="flex items-center gap-2 px-2">
          <span className="text-xs text-slate-400">Duration: Days</span>
        </div>
        <div className="w-px h-5 bg-slate-700 mx-1" />
        
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-slate-700">
              <Link2 className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Link Tasks</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-slate-700">
              <HelpCircle className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Help</TooltipContent>
        </Tooltip>
      </div>
      
      {/* Grid */}
      <div 
        ref={gridRef} 
        className="overflow-auto focus:outline-none focus:ring-1 focus:ring-[#1E4E8C]/50" 
        style={{ maxHeight: 'calc(100vh - 380px)' }} 
        tabIndex={0}
        onKeyDown={handleGridKeyDown}
        data-testid="wbs-grid"
      >
        <table className="w-full text-sm border-collapse">
          <thead className="bg-[#0a0f1e] sticky top-0 z-10">
            <tr>
              <th className="w-6 p-1 text-center text-xs font-medium text-slate-500 border-r border-slate-700/30"></th>
              <th className="w-8 p-2 text-left text-xs font-medium text-slate-500 border-r border-slate-700/30">#</th>
              {columns.map((col, idx) => (
                <th 
                  key={col.id} 
                  className={`p-2 text-left text-xs font-medium text-slate-500 border-r border-slate-700/30 whitespace-nowrap ${
                    idx === focusedCol ? 'bg-slate-800/50' : ''
                  }`}
                  style={{ width: col.width, minWidth: col.width }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleTasks.map((task, rowIdx) => {
              const isMultiSelected = selectedRows.has(task.id);
              return (
              <tr
                key={task.id}
                data-testid={`wbs-row-${task.uid}`}
                draggable
                onDragStart={(e) => handleDragStart(e, task)}
                onDragOver={(e) => handleDragOver(e, task)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, task)}
                onDragEnd={handleDragEnd}
                className={`border-b border-slate-700/20 hover:bg-slate-800/30 cursor-pointer transition-colors ${
                  selectedRow === task.id ? 'bg-[#1a3a5c]/50' : ''
                } ${isMultiSelected ? 'bg-[#1E4E8C]/30 ring-1 ring-[#1E4E8C]/50' : ''}
                ${task.is_critical ? 'border-l-2 border-l-red-500' : 'border-l-2 border-l-transparent'}
                ${dropTarget === task.id ? 'bg-[#0E7C6B]/20 border-t-2 border-t-[#0E7C6B]' : ''}`}
                onClick={(e) => { 
                  // Shift+click for range selection
                  if (e.shiftKey && selectedRow) {
                    const startIdx = visibleTasks.findIndex(t => t.id === selectedRow);
                    const endIdx = rowIdx;
                    const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
                    const newSelection = new Set();
                    for (let i = from; i <= to; i++) {
                      newSelection.add(visibleTasks[i].id);
                    }
                    setSelectedRows(newSelection);
                  } else if (e.ctrlKey || e.metaKey) {
                    // Ctrl/Cmd+click to toggle selection
                    setSelectedRows(prev => {
                      const newSet = new Set(prev);
                      if (newSet.has(task.id)) {
                        newSet.delete(task.id);
                      } else {
                        newSet.add(task.id);
                      }
                      return newSet;
                    });
                  } else {
                    // Regular click - clear multi-select
                    setSelectedRows(new Set());
                  }
                  setSelectedRow(task.id); 
                  setFocusedRow(rowIdx);
                  // Focus the grid to enable keyboard shortcuts
                  gridRef.current?.focus();
                }}
                onContextMenu={(e) => handleContextMenu(e, task)}
              >
                {/* Drag handle */}
                <td className="p-1 text-center border-r border-slate-700/30 bg-[#0c1222] cursor-grab">
                  <GripVertical className="w-3 h-3 text-slate-600 mx-auto" />
                </td>
                {/* Row number */}
                <td className="p-2 text-slate-600 text-xs border-r border-slate-700/30 text-center bg-[#0c1222]">
                  {rowIdx + 1}
                </td>
                
                {columns.map((col, colIdx) => {
                  const isEditable = col.editable && isFieldEditable(task, col.id);
                  const isReadOnly = !isEditable || (task.is_summary && ['percent_complete', 'weightage', 'duration', 'scheduled_start', 'scheduled_finish', 'predecessors'].includes(col.id));
                  
                  return (
                  <td
                    key={col.id}
                    className={`p-0 border-r border-slate-700/30 ${
                      focusedRow === rowIdx && focusedCol === colIdx ? 'ring-2 ring-[#1E4E8C] ring-inset' : ''
                    } ${isReadOnly ? 'bg-slate-900/30' : ''}`}
                    onClick={() => handleCellClick(task, col, rowIdx, colIdx)}
                    onDoubleClick={() => {
                      if (isEditable && !['predecessor', 'resource', 'calendar', 'constraint', 'milestone'].includes(col.type)) {
                        startEditing(task.id, col.id, getCellValue(task, col.id));
                      }
                    }}
                  >
                    {/* Milestone column - checkbox/toggle */}
                    {col.type === 'milestone' ? (
                      <div className="flex items-center justify-center h-full">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!task.is_summary) toggleMilestone(task.id);
                          }}
                          disabled={task.is_summary}
                          className={`w-5 h-5 flex items-center justify-center rounded ${
                            task.is_milestone ? 'text-amber-400' : 'text-slate-600 hover:text-slate-400'
                          } ${task.is_summary ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                          title={task.is_milestone ? 'Milestone' : 'Click to mark as milestone'}
                        >
                          <Flag className={`w-3.5 h-3.5 ${task.is_milestone ? 'fill-amber-400' : ''}`} />
                        </button>
                      </div>
                    ) : /* Duration column - simple days input */
                    col.type === 'duration' && editingCell?.taskId === task.id && editingCell?.field === col.id ? (
                      <div className="flex items-center gap-1 px-1">
                        <input
                          ref={inputRef}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={saveEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
                            if (e.key === 'Escape') cancelEdit();
                            e.stopPropagation();
                          }}
                          className="w-14 h-full px-2 py-1 bg-slate-900 border-none outline-none text-white text-sm"
                          placeholder="e.g. 5d"
                          autoFocus
                        />
                        <span className="text-slate-500 text-xs">days</span>
                      </div>
                    ) : editingCell?.taskId === task.id && editingCell?.field === col.id ? (
                      // Date fields get a date picker
                      col.type === 'date' ? (
                        <DateEditCell
                          value={editValue}
                          onChange={setEditValue}
                          onSave={saveEdit}
                          onCancel={cancelEdit}
                        />
                      ) : (
                        <input
                          ref={inputRef}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={saveEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
                            if (e.key === 'Escape') cancelEdit();
                            e.stopPropagation();
                          }}
                          className="w-full h-full px-2 py-1.5 bg-slate-900 border-none outline-none text-white text-sm"
                          autoFocus
                        />
                      )
                    ) : (
                      <div 
                        className={`px-2 py-1.5 text-sm truncate ${
                          col.id === 'name' && task.is_summary ? 'font-semibold' : ''
                        } ${col.id === 'name' && task.is_critical ? 'text-red-300' : 'text-slate-300'}
                        ${['predecessor', 'resource', 'calendar', 'constraint'].includes(col.type) && !task.is_summary ? 'cursor-pointer hover:bg-slate-800/50' : ''}
                        ${isReadOnly ? 'text-slate-500 italic' : ''}`}
                        style={col.id === 'name' ? { paddingLeft: `${6 + (task.outline_level - 1) * 10}px` } : {}}
                      >
                        {col.id === 'name' && task.is_summary && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedTasks(prev => {
                                const next = new Set(prev);
                                if (next.has(task.id)) next.delete(task.id);
                                else next.add(task.id);
                                return next;
                              });
                            }}
                            className="mr-1 inline-flex items-center justify-center w-4 h-4"
                          >
                            {expandedTasks.has(task.id) ? 
                              <ChevronDown className="w-3 h-3 text-slate-400" /> : 
                              <ChevronRight className="w-3 h-3 text-slate-400" />
                            }
                          </button>
                        )}
                        {col.id === 'percent_complete' || col.type === 'percentage' ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden max-w-[40px]">
                              <div className="h-full bg-[#0E7C6B]" style={{ width: `${parseFloat(getCellValue(task, col.id)) || 0}%` }} />
                            </div>
                            <span className={`text-xs ${isReadOnly ? 'text-slate-500' : 'text-slate-400'}`}>{getCellValue(task, col.id)}</span>
                          </div>
                        ) : (
                          getCellValue(task, col.id)
                        )}
                      </div>
                    )}
                  </td>
                );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Add task row */}
      <div 
        className="flex items-center gap-2 px-4 py-3 border-t border-slate-700/30 text-slate-600 hover:text-slate-400 hover:bg-slate-800/30 cursor-pointer transition-colors"
        onClick={() => addTask(visibleTasks[visibleTasks.length - 1]?.id)}
        data-testid="add-task-row"
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm italic">Click to add a new task...</span>
      </div>

      {/* Predecessor Selection Dialog - Enhanced with Type & Lag */}
      <Dialog open={showPredecessorDialog} onOpenChange={setShowPredecessorDialog}>
        <DialogContent className="bg-[#0c1222] border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Define Predecessors</DialogTitle>
            <p className="text-sm text-slate-500">Select tasks, dependency type (FS/SS/FF/SF), and lag (lead/delay).</p>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2 py-4">
            {tasks.filter(t => t.id !== dialogTask?.id && !t.is_summary).map(t => {
              const isSelected = selectedPredecessors.some(p => p.predecessor_id === t.id);
              const depConfig = selectedPredecessors.find(p => p.predecessor_id === t.id);
              
              return (
                <div key={t.id} className={`p-3 rounded border transition-colors ${
                  isSelected ? 'border-[#0E7C6B] bg-[#0E7C6B]/10' : 'border-slate-700 hover:border-slate-600'
                }`}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => togglePredecessor(t.id, e.target.checked)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-[#0E7C6B]"
                    />
                    <span className="text-slate-300 text-sm flex-1">{t.uid}. {t.name}</span>
                  </label>
                  
                  {/* Type and Lag options - only show when selected */}
                  {isSelected && (
                    <div className="mt-3 pl-7 flex items-center gap-3">
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Type</Label>
                        <Select 
                          value={depConfig?.type || 'finish_to_start'} 
                          onValueChange={(v) => updatePredecessorType(t.id, v)}
                        >
                          <SelectTrigger className="w-20 h-7 text-xs bg-slate-800 border-slate-600">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600">
                            <SelectItem value="finish_to_start">FS</SelectItem>
                            <SelectItem value="start_to_start">SS</SelectItem>
                            <SelectItem value="finish_to_finish">FF</SelectItem>
                            <SelectItem value="start_to_finish">SF</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Lag</Label>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={depConfig?.lag || 0}
                            onChange={(e) => updatePredecessorLag(t.id, e.target.value, depConfig?.lag_unit)}
                            className="w-16 h-7 text-xs bg-slate-800 border-slate-600 text-white"
                            placeholder="0"
                          />
                          <Select 
                            value={depConfig?.lag_unit || 'hours'} 
                            onValueChange={(v) => updatePredecessorLag(t.id, depConfig?.lag, v)}
                          >
                            <SelectTrigger className="w-14 h-7 text-xs bg-slate-800 border-slate-600">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-600">
                              <SelectItem value="hours">h</SelectItem>
                              <SelectItem value="days">d</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 ml-2">
                        {parseInt(depConfig?.lag || 0) > 0 ? '(delay)' : parseInt(depConfig?.lag || 0) < 0 ? '(lead)' : ''}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-xs text-slate-500 border-t border-slate-700 pt-3">
            <strong>Types:</strong> FS=Finish-to-Start, SS=Start-to-Start, FF=Finish-to-Finish, SF=Start-to-Finish
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPredecessorDialog(false)} className="border-slate-600">Cancel</Button>
            <Button onClick={savePredecessors} className="bg-[#0E7C6B] text-black">Save Dependencies</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resource Selection Dialog */}
      <Dialog open={showResourceDialog} onOpenChange={setShowResourceDialog}>
        <DialogContent className="bg-[#0c1222] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Assign Resources</DialogTitle>
            <p className="text-sm text-slate-500">Select resources to assign to this task.</p>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto space-y-1 py-4">
            {resources.map(r => (
              <label key={r.id} className="flex items-center gap-3 p-2 hover:bg-slate-800 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedResources.includes(r.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedResources([...selectedResources, r.id]);
                    } else {
                      setSelectedResources(selectedResources.filter(id => id !== r.id));
                    }
                  }}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-[#0E7C6B]"
                />
                <span className="text-slate-300 text-sm">{r.name}</span>
                <Badge variant="outline" className="text-xs border-slate-600">{r.type}</Badge>
                <span className="text-slate-500 text-xs ml-auto">${r.standard_rate}/hr</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResourceDialog(false)} className="border-slate-600">Cancel</Button>
            <Button onClick={saveResources} className="bg-[#0E7C6B] text-black">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calendar Selection Dialog */}
      <Dialog open={showCalendarSelect} onOpenChange={setShowCalendarSelect}>
        <DialogContent className="bg-[#0c1222] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Assign Calendar</DialogTitle>
            <p className="text-sm text-slate-500">Select a calendar for this task, or remove assignment.</p>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {/* No Calendar Option */}
            <label className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-colors ${
              selectedCalendar === '' ? 'border-amber-500 bg-amber-500/10' : 'border-slate-700 hover:border-slate-600'
            }`}>
              <input
                type="radio"
                name="calendar"
                checked={selectedCalendar === ''}
                onChange={() => setSelectedCalendar('')}
                className="w-4 h-4 text-amber-500"
              />
              <div className="flex items-center gap-2">
                <X className="w-4 h-4 text-amber-500" />
                <div>
                  <div className="text-white text-sm">No Calendar (Use Default)</div>
                  <div className="text-slate-500 text-xs">Uses project default calendar settings</div>
                </div>
              </div>
            </label>
            
            <div className="h-px bg-slate-700/50 my-2" />
            
            {calendars.length === 0 ? (
              <div className="text-center py-4 text-slate-500 text-sm">
                No calendars available. Create one in the Calendars tab.
              </div>
            ) : (
              calendars.map(cal => (
                <label key={cal.id} className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-colors ${
                  selectedCalendar === cal.id ? 'border-[#0E7C6B] bg-[#0E7C6B]/10' : 'border-slate-700 hover:border-slate-600'
                }`}>
                  <input
                    type="radio"
                    name="calendar"
                    checked={selectedCalendar === cal.id}
                    onChange={() => setSelectedCalendar(cal.id)}
                    className="w-4 h-4 text-[#0E7C6B]"
                  />
                  <div className="flex-1">
                    <div className="text-white text-sm">{cal.name}</div>
                    <div className="text-slate-500 text-xs flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      {cal.start_time || '09:00'} - {cal.end_time || '17:00'}
                      <span className="mx-1">•</span>
                      {cal.hours_per_day || 8}h/day
                    </div>
                  </div>
                  {cal.is_base && (
                    <Badge className="bg-slate-800 text-slate-300 border-slate-600 text-xs">Base</Badge>
                  )}
                </label>
              ))
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowCalendarSelect(false)} className="border-slate-600">Cancel</Button>
            <Button onClick={saveCalendar} className="bg-[#0E7C6B] text-black">
              {selectedCalendar === '' ? 'Remove Calendar' : 'Assign Calendar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Constraint Dialog */}
      <Dialog open={showConstraintDialog} onOpenChange={setShowConstraintDialog}>
        <DialogContent className="bg-[#0c1222] border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Set Constraint</DialogTitle>
            <p className="text-sm text-slate-500">Define scheduling constraint for this task.</p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Constraint Type</Label>
              <Select value={selectedConstraint.type} onValueChange={(v) => setSelectedConstraint(p => ({...p, type: v}))}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="as_soon_as_possible">As Soon As Possible (ASAP)</SelectItem>
                  <SelectItem value="as_late_as_possible">As Late As Possible (ALAP)</SelectItem>
                  <SelectItem value="must_start_on">Must Start On (MSO)</SelectItem>
                  <SelectItem value="must_finish_on">Must Finish On (MFO)</SelectItem>
                  <SelectItem value="start_no_earlier_than">Start No Earlier Than (SNET)</SelectItem>
                  <SelectItem value="start_no_later_than">Start No Later Than (SNLT)</SelectItem>
                  <SelectItem value="finish_no_earlier_than">Finish No Earlier Than (FNET)</SelectItem>
                  <SelectItem value="finish_no_later_than">Finish No Later Than (FNLT)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {constraintRequiresDate(selectedConstraint.type) && (
              <div>
                <Label className="text-slate-300">Constraint Date</Label>
                <Input
                  type="date"
                  value={selectedConstraint.date}
                  onChange={(e) => setSelectedConstraint(p => ({...p, date: e.target.value}))}
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConstraintDialog(false)} className="border-slate-600">Cancel</Button>
            <Button onClick={saveConstraint} className="bg-[#0E7C6B] text-black">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================
// BASELINES TAB COMPONENT
// ============================================
function BaselinesTab({ baselines, comparison, onSave, onCompare, formatDate, formatDuration }) {
  return (
    <div className="space-y-4">
      <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-700/30">
          <div>
            <h3 className="text-lg font-semibold text-white">Baseline Management</h3>
            <p className="text-sm text-slate-500">Track schedule changes over time</p>
          </div>
          <Button onClick={onSave} className="bg-[#1E4E8C]">
            <Plus className="w-4 h-4 mr-2" /> Save New Baseline
          </Button>
        </div>
        
        {baselines.length === 0 ? (
          <div className="p-8 text-center">
            <Flag className="w-12 h-12 mx-auto text-slate-700 mb-3" />
            <p className="text-slate-400">No baselines saved</p>
            <p className="text-sm text-slate-600 mt-1">Save your first baseline to track schedule changes</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700/30">
            {baselines.map((b, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 hover:bg-slate-800/30">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-slate-600">Baseline {b.baseline_number}</Badge>
                    <span className="font-medium text-white">{b.name}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    Saved {formatDate(b.captured_at)} - Duration: {formatDuration(b.duration)} - Cost: ${b.cost?.toLocaleString() || 0}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => onCompare(b.baseline_number)} className="border-slate-600">
                  Compare
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {comparison && (
        <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
          <h4 className="font-semibold text-white mb-4">Comparison: {comparison.baseline_name}</h4>
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="bg-[#0a0f1e] rounded-lg p-3">
              <div className="text-xs text-slate-500">Finish Variance</div>
              <div className={`text-xl font-bold ${comparison.project_variance.finish_variance_days > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {comparison.project_variance.finish_variance_days > 0 ? '+' : ''}{comparison.project_variance.finish_variance_days}d
              </div>
            </div>
            <div className="bg-[#0a0f1e] rounded-lg p-3">
              <div className="text-xs text-slate-500">Duration Variance</div>
              <div className={`text-xl font-bold ${comparison.project_variance.duration_variance > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {formatDuration(comparison.project_variance.duration_variance)}
              </div>
            </div>
            <div className="bg-[#0a0f1e] rounded-lg p-3">
              <div className="text-xs text-slate-500">Cost Variance</div>
              <div className={`text-xl font-bold ${comparison.project_variance.cost_variance > 0 ? 'text-red-400' : 'text-green-400'}`}>
                ${comparison.project_variance.cost_variance.toLocaleString()}
              </div>
            </div>
            <div className="bg-[#0a0f1e] rounded-lg p-3">
              <div className="text-xs text-slate-500">Delayed Tasks</div>
              <div className="text-xl font-bold text-amber-400">
                {comparison.tasks.filter(t => t.is_delayed).length}
              </div>
            </div>
          </div>
          <ScrollArea className="h-60">
            <table className="w-full text-sm">
              <thead className="bg-[#0a0f1e]">
                <tr>
                  <th className="p-2 text-left text-xs text-slate-500">Task</th>
                  <th className="p-2 text-left text-xs text-slate-500">Baseline</th>
                  <th className="p-2 text-left text-xs text-slate-500">Current</th>
                  <th className="p-2 text-left text-xs text-slate-500">Variance</th>
                </tr>
              </thead>
              <tbody>
                {comparison.tasks.map(t => (
                  <tr key={t.id} className="border-b border-slate-700/30">
                    <td className="p-2 text-slate-300">{t.name}</td>
                    <td className="p-2 text-slate-500">{formatDate(t.baseline_finish)}</td>
                    <td className="p-2 text-slate-300">{formatDate(t.current_finish)}</td>
                    <td className={`p-2 ${t.finish_variance_days > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {t.finish_variance_days > 0 ? '+' : ''}{t.finish_variance_days}d
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

// ============================================
// CALENDARS TAB COMPONENT - Enhanced
// ============================================
function CalendarsTab({ projectId, onRefresh }) {
  const { can: canPerm } = usePermissions();
  const canSchedCreate = canPerm('MACRO_SCHEDULE', 'create');
  const canSchedConfigure = canPerm('MACRO_SCHEDULE', 'configure');
  const canSchedDelete = canPerm('MACRO_SCHEDULE', 'delete');
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [newCalendar, setNewCalendar] = useState({
    name: '',
    working_days: [1, 2, 3, 4, 5],
    start_time: '09:00',
    end_time: '17:00',
    hours_per_day: 8,
    exceptions: []
  });
  const [showExceptionDialog, setShowExceptionDialog] = useState(false);
  const [newException, setNewException] = useState({
    name: '',
    start_date: '',
    end_date: '',
    is_working: false
  });
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Fetch calendars
  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/scheduling/projects/${projectId}/calendars`);
        setCalendars(res.data || []);
      } catch (err) {
        console.error('Failed to fetch calendars:', err);
        toast.error('Failed to load calendars');
      } finally {
        setLoading(false);
      }
    };
    fetchCalendars();
  }, [projectId]);
  
  const toggleWorkingDay = (dayIndex) => {
    setNewCalendar(prev => {
      const days = [...prev.working_days];
      if (days.includes(dayIndex)) {
        return { ...prev, working_days: days.filter(d => d !== dayIndex) };
      } else {
        return { ...prev, working_days: [...days, dayIndex].sort() };
      }
    });
  };
  
  const createCalendar = async () => {
    if (!newCalendar.name.trim()) {
      toast.error('Please enter a calendar name');
      return;
    }
    try {
      await api.post('/scheduling/calendars', {
        ...newCalendar,
        project_id: projectId,
        is_base: calendars.length === 0
      });
      toast.success('Calendar created');
      setShowCreateDialog(false);
      setNewCalendar({
        name: '',
        working_days: [1, 2, 3, 4, 5],
        start_time: '09:00',
        end_time: '17:00',
        hours_per_day: 8,
        exceptions: []
      });
      // Refresh calendars
      const res = await api.get(`/scheduling/projects/${projectId}/calendars`);
      setCalendars(res.data || []);
    } catch (err) {
      toast.error('Failed to create calendar');
    }
  };
  
  const copyCalendar = async (calendarId) => {
    try {
      await api.post(`/scheduling/calendars/${calendarId}/copy`);
      toast.success('Calendar copied');
      const res = await api.get(`/scheduling/projects/${projectId}/calendars`);
      setCalendars(res.data || []);
    } catch (err) {
      toast.error('Failed to copy calendar');
    }
  };
  
  const deleteCalendar = async (calendarId) => {
    try {
      await api.delete(`/scheduling/calendars/${calendarId}`);
      toast.success('Calendar deleted');
      setCalendars(calendars.filter(c => c.id !== calendarId));
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete calendar'));
    }
  };
  
  const openEditCalendar = (calendar) => {
    setEditingCalendar(calendar);
    setNewCalendar({
      name: calendar.name || '',
      working_days: calendar.working_days || [1, 2, 3, 4, 5],
      start_time: calendar.start_time || calendar.default_hours?.start || '09:00',
      end_time: calendar.end_time || calendar.default_hours?.end || '17:00',
      hours_per_day: calendar.hours_per_day || 8,
      exceptions: calendar.exceptions || []
    });
    setShowCreateDialog(true);
  };
  
  const updateCalendar = async () => {
    if (!newCalendar.name.trim()) {
      toast.error('Please enter a calendar name');
      return;
    }
    try {
      await api.put(`/scheduling/calendars/${editingCalendar.id}`, {
        name: newCalendar.name,
        working_days: newCalendar.working_days,
        start_time: newCalendar.start_time,
        end_time: newCalendar.end_time,
        hours_per_day: newCalendar.hours_per_day,
        exceptions: newCalendar.exceptions
      });
      toast.success('Calendar updated');
      setShowCreateDialog(false);
      setEditingCalendar(null);
      setNewCalendar({
        name: '',
        working_days: [1, 2, 3, 4, 5],
        start_time: '09:00',
        end_time: '17:00',
        hours_per_day: 8,
        exceptions: []
      });
      // Refresh calendars
      const res = await api.get(`/scheduling/projects/${projectId}/calendars`);
      setCalendars(res.data || []);
    } catch (err) {
      toast.error('Failed to update calendar');
    }
  };
  
  const handleDialogClose = (open) => {
    if (!open) {
      setEditingCalendar(null);
      setNewCalendar({
        name: '',
        working_days: [1, 2, 3, 4, 5],
        start_time: '09:00',
        end_time: '17:00',
        hours_per_day: 8,
        exceptions: []
      });
    }
    setShowCreateDialog(open);
  };
  
  const addException = () => {
    if (!newException.name || !newException.start_date || !newException.end_date) {
      toast.error('Please fill all exception fields');
      return;
    }
    setNewCalendar(prev => ({
      ...prev,
      exceptions: [...prev.exceptions, { ...newException, id: Date.now().toString() }]
    }));
    setNewException({ name: '', start_date: '', end_date: '', is_working: false });
    setShowExceptionDialog(false);
  };
  
  const removeException = (exceptionId) => {
    setNewCalendar(prev => ({
      ...prev,
      exceptions: prev.exceptions.filter(e => e.id !== exceptionId)
    }));
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#0E7C6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Project Calendars</h2>
        {canSchedConfigure && <Button 
          onClick={() => setShowCreateDialog(true)} 
          className="bg-[#0E7C6B] hover:bg-[#00C589] text-black font-medium"
          data-testid="new-calendar-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Calendar
        </Button>}
      </div>
      
      {/* Calendar Grid */}
      {calendars.length === 0 ? (
        <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-12 text-center">
          <Calendar className="w-16 h-16 mx-auto text-slate-700 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Calendars Yet</h3>
          <p className="text-slate-500 mb-4">Create your first calendar to define working hours and exceptions.</p>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-[#0E7C6B] hover:bg-[#00C589] text-black">
            <Plus className="w-4 h-4 mr-2" /> Create Calendar
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {calendars.map(cal => (
            <div 
              key={cal.id} 
              className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4 hover:border-slate-600 transition-colors"
              data-testid={`calendar-card-${cal.id}`}
            >
              {/* Calendar Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#0E7C6B]" />
                  <span className="font-medium text-white">{cal.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => openEditCalendar(cal)}
                    className="h-8 w-8 p-0 hover:bg-slate-700"
                    data-testid={`edit-calendar-${cal.id}`}
                  >
                    <Edit className="w-4 h-4 text-slate-400" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => copyCalendar(cal.id)}
                    className="h-8 w-8 p-0 hover:bg-slate-700"
                  >
                    <Copy className="w-4 h-4 text-slate-400" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => deleteCalendar(cal.id)}
                    className="h-8 w-8 p-0 hover:bg-slate-700 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4 text-slate-400" />
                  </Button>
                </div>
              </div>
              
              {/* Base Calendar Badge */}
              {cal.is_base && (
                <Badge className="bg-slate-800 text-slate-300 border-slate-600 mb-3">Base Calendar</Badge>
              )}
              
              {/* Working Hours */}
              <div className="flex items-center gap-2 text-slate-400 mb-3">
                <Clock className="w-4 h-4" />
                <span className="text-sm">
                  {cal.start_time || '09:00'} - {cal.end_time || '17:00'} ({cal.hours_per_day || 8}h/day)
                </span>
              </div>
              
              {/* Working Days */}
              <div className="flex items-center gap-1 mb-3">
                {dayNames.map((day, idx) => (
                  <span 
                    key={idx}
                    className={`px-2 py-1 text-xs rounded ${
                      cal.working_days?.includes(idx) 
                        ? 'bg-[#0E7C6B]/20 text-[#0E7C6B] border border-[#0E7C6B]/50' 
                        : 'bg-slate-800 text-slate-500 border border-slate-700'
                    }`}
                  >
                    {day}
                  </span>
                ))}
              </div>
              
              {/* Stats */}
              <div className="text-xs text-slate-500">
                {cal.exceptions?.length || 0} exceptions • {cal.tasks_using || 0} tasks using
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Create/Edit Calendar Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="bg-[#0c1222] border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">
              {editingCalendar ? 'Edit Calendar' : 'Create New Calendar'}
            </DialogTitle>
            <p className="text-sm text-slate-500">Define working days, hours, and exceptions for this calendar.</p>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Calendar Name */}
            <div>
              <Label className="text-slate-300 mb-2 block">Calendar Name</Label>
              <Input
                value={newCalendar.name}
                onChange={(e) => setNewCalendar(p => ({...p, name: e.target.value}))}
                placeholder="e.g., Standard, Night Shift, 24/7"
                className="bg-[#0a0f1e] border-[#0E7C6B] text-white focus:ring-[#0E7C6B]"
                data-testid="calendar-name-input"
              />
            </div>
            
            {/* Working Days */}
            <div>
              <Label className="text-slate-300 mb-2 block">Working Days</Label>
              <div className="flex items-center gap-2">
                {dayNames.map((day, idx) => (
                  <button
                    key={idx}
                    onClick={() => toggleWorkingDay(idx)}
                    className={`px-3 py-2 text-sm rounded-full transition-colors ${
                      newCalendar.working_days.includes(idx)
                        ? 'bg-[#0E7C6B] text-black font-medium'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                    data-testid={`working-day-${day.toLowerCase()}`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Time Settings */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300 mb-2 block">Start Time</Label>
                <div className="relative">
                  <Input
                    type="time"
                    value={newCalendar.start_time}
                    onChange={(e) => setNewCalendar(p => ({...p, start_time: e.target.value}))}
                    className="bg-[#0a0f1e] border-slate-700 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 mb-2 block">End Time</Label>
                <div className="relative">
                  <Input
                    type="time"
                    value={newCalendar.end_time}
                    onChange={(e) => setNewCalendar(p => ({...p, end_time: e.target.value}))}
                    className="bg-[#0a0f1e] border-slate-700 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 mb-2 block">Hours/Day</Label>
                <Input
                  type="number"
                  value={newCalendar.hours_per_day}
                  onChange={(e) => setNewCalendar(p => ({...p, hours_per_day: parseInt(e.target.value) || 8}))}
                  className="bg-[#0a0f1e] border-slate-700 text-white"
                  min={1}
                  max={24}
                />
              </div>
            </div>
            
            {/* Calendar Exceptions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-slate-300">Calendar Exceptions (Holidays, Special Days)</Label>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setShowExceptionDialog(true)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Exception
                </Button>
              </div>
              
              {newCalendar.exceptions.length === 0 ? (
                <div className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center">
                  <span className="text-slate-500 text-sm">No exceptions defined</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {newCalendar.exceptions.map(exc => (
                    <div key={exc.id} className="flex items-center justify-between bg-slate-800 rounded-lg px-3 py-2">
                      <div>
                        <span className="text-white text-sm">{exc.name}</span>
                        <span className="text-slate-500 text-xs ml-2">
                          {exc.start_date} - {exc.end_date}
                        </span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => removeException(exc.id)}
                        className="h-6 w-6 p-0 hover:bg-slate-700"
                      >
                        <Trash2 className="w-3 h-3 text-slate-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDialogClose(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={editingCalendar ? updateCalendar : createCalendar} 
              className="bg-[#0E7C6B] hover:bg-[#00C589] text-black font-medium"
              data-testid="save-calendar-btn"
            >
              {editingCalendar ? 'Save Changes' : 'Create Calendar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Exception Dialog */}
      <Dialog open={showExceptionDialog} onOpenChange={setShowExceptionDialog}>
        <DialogContent className="bg-[#0c1222] border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Add Exception</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Exception Name</Label>
              <Input
                value={newException.name}
                onChange={(e) => setNewException(p => ({...p, name: e.target.value}))}
                placeholder="e.g., Christmas, Company Holiday"
                className="bg-[#0a0f1e] border-slate-700 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Start Date</Label>
                <Input
                  type="date"
                  value={newException.start_date}
                  onChange={(e) => setNewException(p => ({...p, start_date: e.target.value}))}
                  className="bg-[#0a0f1e] border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">End Date</Label>
                <Input
                  type="date"
                  value={newException.end_date}
                  onChange={(e) => setNewException(p => ({...p, end_date: e.target.value}))}
                  className="bg-[#0a0f1e] border-slate-700 text-white"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExceptionDialog(false)} className="border-slate-600">Cancel</Button>
            <Button onClick={addException} className="bg-[#0E7C6B] text-black">Add Exception</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================
// RESOURCES TAB COMPONENT
// ============================================
function ResourcesTab({ resources, tasks, projectId, onRefresh }) {
  const { can: canPerm } = usePermissions();
  const canSchedCreate = canPerm('MACRO_SCHEDULE', 'create');
  const canSchedEdit = canPerm('MACRO_SCHEDULE', 'edit');
  const canSchedDelete = canPerm('MACRO_SCHEDULE', 'delete');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [newResource, setNewResource] = useState({ name: '', type: 'work', standard_rate: 0, max_units: 100, initials: '' });
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [levellingLoading, setLevellingLoading] = useState(false);
  const [levellingSuggestions, setLevellingSuggestions] = useState(null);
  const [showLevellingDialog, setShowLevellingDialog] = useState(false);
  const [applyingLevel, setApplyingLevel] = useState(false);

  const loadLevellingSuggestions = React.useCallback(async () => {
    if (!projectId) return;
    setLevellingLoading(true);
    try {
      const res = await api.get('/scheduling/resources/level-suggestions', { params: { project_id: projectId } });
      setLevellingSuggestions(res.data);
      setShowLevellingDialog(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to compute levelling suggestions');
    } finally {
      setLevellingLoading(false);
    }
  }, [projectId]);

  const applyLevelling = async (selectedIds) => {
    if (!levellingSuggestions || !selectedIds || selectedIds.length === 0) return;
    const toApply = (levellingSuggestions.suggestions || []).filter(s => selectedIds.includes(s.assignment_id));
    setApplyingLevel(true);
    try {
      const res = await api.post('/scheduling/resources/apply-levelling', { project_id: projectId, shifts: toApply });
      toast.success(`Levelled ${res.data.applied} task(s) — peak dropped by ~${res.data.hoursSaved || 0}h`);
      setShowLevellingDialog(false);
      setLevellingSuggestions(null);
      onRefresh();
      loadAnalysis();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply levelling');
    } finally {
      setApplyingLevel(false);
    }
  };

  const loadAnalysis = React.useCallback(async () => {
    if (!projectId) return;
    setAnalysisLoading(true);
    try {
      const res = await api.get(`/scheduling/resources/loading-analysis`, { params: { project_id: projectId } });
      setAnalysis(res.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Resource analysis failed', err?.response?.data || err.message);
    } finally {
      setAnalysisLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { loadAnalysis(); }, [loadAnalysis, resources.length]);
  
  const addResource = async () => {
    try {
      if (editingResource) {
        await api.put(`/scheduling/resources/${editingResource.id}`, newResource);
        toast.success('Resource updated');
      } else {
        await api.post('/scheduling/resources', { ...newResource, project_id: projectId });
        toast.success('Resource added');
      }
      setShowAddDialog(false);
      setEditingResource(null);
      setNewResource({ name: '', type: 'work', standard_rate: 0, max_units: 100, initials: '' });
      onRefresh();
      loadAnalysis();
    } catch (err) {
      toast.error(editingResource ? 'Failed to update resource' : 'Failed to add resource');
    }
  };
  
  return (
    <div className="space-y-4">
      {/* ═══ Resource Loading Impact Analysis ═══ */}
      <div className="bg-white dark:bg-gradient-to-br dark:from-[#0c1222] dark:to-[#0a0f1e] border border-slate-200 dark:border-slate-700/50 rounded-xl p-4 shadow-sm" data-testid="resource-analysis-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-500 dark:text-amber-400" /> Resource Loading Impact
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-500">Impact of resource allocation on the overall schedule</p>
          </div>
          <div className="flex items-center gap-2">
            {analysis && analysis.overallocatedCount > 0 && canSchedEdit && (
              <Button size="sm" onClick={() => loadLevellingSuggestions()} disabled={levellingLoading} className="bg-amber-500 hover:bg-amber-600 text-white h-8" data-testid="resource-level-btn">
                <Zap className="w-3.5 h-3.5 mr-1" /> {levellingLoading ? 'Computing…' : 'Level to Capacity'}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={loadAnalysis} disabled={analysisLoading} className="border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 h-8" data-testid="resource-analysis-refresh">
              {analysisLoading ? 'Analyzing…' : 'Re-analyze'}
            </Button>
          </div>
        </div>
        {!analysis && !analysisLoading && (
          <div className="text-sm text-slate-500 py-3">No analysis yet. Click Re-analyze.</div>
        )}
        {analysis && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
              <div className="bg-slate-50 dark:bg-[#0a0f1e] border border-slate-200 dark:border-slate-700/40 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Resources</div>
                <div className="text-xl font-bold text-slate-800 dark:text-white mt-1" data-testid="analysis-resource-count">{analysis.resourceCount}</div>
              </div>
              <div className="bg-slate-50 dark:bg-[#0a0f1e] border border-slate-200 dark:border-slate-700/40 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Assignments</div>
                <div className="text-xl font-bold text-slate-800 dark:text-white mt-1">{analysis.assignmentCount}</div>
              </div>
              <div className="bg-red-50 dark:bg-[#0a0f1e] border border-red-300 dark:border-red-500/30 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-red-600 dark:text-red-400">Overallocated</div>
                <div className="text-xl font-bold text-red-600 dark:text-red-300 mt-1" data-testid="analysis-overallocated-count">{analysis.overallocatedCount}</div>
              </div>
              <div className="bg-amber-50 dark:bg-[#0a0f1e] border border-amber-300 dark:border-amber-500/30 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400">Excess Hours</div>
                <div className="text-xl font-bold text-amber-600 dark:text-amber-300 mt-1">{analysis.totalOverHours}h</div>
              </div>
              <div className="bg-fuchsia-50 dark:bg-[#0a0f1e] border border-fuchsia-300 dark:border-fuchsia-500/30 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-fuchsia-600 dark:text-fuchsia-400">Est. Slip</div>
                <div className="text-xl font-bold text-fuchsia-600 dark:text-fuchsia-300 mt-1" data-testid="analysis-schedule-slip">{analysis.estimatedScheduleSlipDays}d</div>
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-[#0a0f1e] border border-slate-200 dark:border-slate-700/40 rounded-lg p-3 mb-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              {analysis.narrative}
            </div>
            {(analysis.overallocated || []).length > 0 && (
              <div className="bg-slate-50 dark:bg-[#0a0f1e] border border-slate-200 dark:border-slate-700/40 rounded-lg overflow-hidden">
                <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-700/40">Top Overallocated Resources</div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 dark:bg-slate-800/40">
                    <tr>
                      <th className="p-2 text-left text-slate-600 dark:text-slate-400">Resource</th>
                      <th className="p-2 text-right text-slate-600 dark:text-slate-400">Capacity/day</th>
                      <th className="p-2 text-right text-slate-600 dark:text-slate-400">Peak/day</th>
                      <th className="p-2 text-right text-slate-600 dark:text-slate-400">Utilisation</th>
                      <th className="p-2 text-right text-slate-600 dark:text-slate-400">Over-days</th>
                      <th className="p-2 text-right text-slate-600 dark:text-slate-400">Excess</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.overallocated.slice(0, 6).map(r => (
                      <tr key={r.resource_id} className="border-t border-slate-200 dark:border-slate-700/30">
                        <td className="p-2 text-slate-800 dark:text-white">{r.name}</td>
                        <td className="p-2 text-right text-slate-700 dark:text-slate-300">{r.capacity_h_per_day}h</td>
                        <td className="p-2 text-right text-red-600 dark:text-red-300">{r.peak_h_per_day}h</td>
                        <td className="p-2 text-right text-slate-700 dark:text-slate-300">{r.utilization_pct}%</td>
                        <td className="p-2 text-right text-slate-700 dark:text-slate-300">{r.over_days_count}</td>
                        <td className="p-2 text-right text-amber-600 dark:text-amber-300">{r.over_hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl">
      <div className="flex items-center justify-between p-4 border-b border-slate-700/30">
        <div>
          <h3 className="text-lg font-semibold text-white">Resource Pool</h3>
          <p className="text-sm text-slate-500">Manage resources and allocations</p>
        </div>
        {canSchedCreate && <Button onClick={() => setShowAddDialog(true)} className="bg-[#1E4E8C]"><Plus className="w-4 h-4 mr-2" /> Add Resource</Button>}
      </div>
      
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0f1e]">
            <tr>
              <th className="p-3 text-left text-xs text-slate-500">Name</th>
              <th className="p-3 text-left text-xs text-slate-500">Type</th>
              <th className="p-3 text-right text-xs text-slate-500">Rate</th>
              <th className="p-3 text-right text-xs text-slate-500">Max Units</th>
              <th className="p-3 text-left text-xs text-slate-500">Allocation</th>
              <th className="p-3 text-center text-xs text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {resources.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-600">No resources</td></tr>
            ) : (
              resources.map(r => (
                <tr key={r.id} className="border-b border-slate-700/30 hover:bg-slate-800/30">
                  <td className="p-3 text-white">{r.name}</td>
                  <td className="p-3 text-slate-400 capitalize">{r.type}</td>
                  <td className="p-3 text-right text-slate-300">${r.standard_rate}/hr</td>
                  <td className="p-3 text-right text-slate-300">{r.max_units}%</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <Progress value={r.current_allocation || 0} className="h-2 flex-1" />
                      <span className="text-xs text-slate-500 w-10">{r.current_allocation || 0}%</span>
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="hover:bg-slate-700" data-testid={`resource-menu-${r.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                        <DropdownMenuItem
                          disabled={!canSchedEdit}
                          onClick={() => {
                            setEditingResource(r);
                            setNewResource({
                              name: r.name || '',
                              type: r.type || 'work',
                              standard_rate: r.standard_rate || 0,
                              max_units: r.max_units || 100,
                              initials: r.initials || ''
                            });
                            setShowAddDialog(true);
                          }}
                          className="text-slate-200 focus:bg-slate-700 focus:text-white cursor-pointer"
                        >
                          <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={!canSchedDelete}
                          onClick={async () => {
                            if (!window.confirm(`Delete resource "${r.name}"? Existing assignments will be unlinked.`)) return;
                            try {
                              await api.delete(`/scheduling/resources/${r.id}`);
                              toast.success(`Deleted ${r.name}`);
                              onRefresh && onRefresh();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Failed to delete resource');
                            }
                          }}
                          className="text-red-400 focus:bg-red-500/10 focus:text-red-300 cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) { setEditingResource(null); setNewResource({ name: '', type: 'work', standard_rate: 0, max_units: 100, initials: '' }); } }}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader><DialogTitle className="text-white">{editingResource ? 'Edit Resource' : 'Add Resource'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3 max-h-[70vh] overflow-y-auto pr-2">
            {/* ── Identity ─────────────────────────────── */}
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Identity</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-xs">Name*</Label>
                <Input value={newResource.name} onChange={(e) => setNewResource(p => ({...p, name: e.target.value}))} className="bg-slate-900 border-slate-600 text-white h-9" data-testid="resource-name-input" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Initials</Label>
                <Input value={newResource.initials || ''} maxLength={3} placeholder="e.g. JS" onChange={(e) => setNewResource(p => ({...p, initials: e.target.value.toUpperCase()}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Email</Label>
                <Input type="email" value={newResource.email || ''} onChange={(e) => setNewResource(p => ({...p, email: e.target.value}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Phone</Label>
                <Input value={newResource.phone || ''} onChange={(e) => setNewResource(p => ({...p, phone: e.target.value}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Department</Label>
                <Input value={newResource.department || ''} placeholder="e.g. Civil / Piping" onChange={(e) => setNewResource(p => ({...p, department: e.target.value}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Location / Site</Label>
                <Input value={newResource.location || ''} onChange={(e) => setNewResource(p => ({...p, location: e.target.value}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Vendor</Label>
                <Input value={newResource.vendor || ''} placeholder="e.g. L&T, Bechtel" onChange={(e) => setNewResource(p => ({...p, vendor: e.target.value}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Group</Label>
                <Input value={newResource.group || ''} onChange={(e) => setNewResource(p => ({...p, group: e.target.value}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
            </div>

            {/* ── Capacity ────────────────────────────── */}
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pt-2">Capacity & Availability</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-300 text-xs">Type</Label>
                <Select value={newResource.type} onValueChange={(v) => setNewResource(p => ({...p, type: v}))}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="work">Work (person)</SelectItem>
                    <SelectItem value="material">Material</SelectItem>
                    <SelectItem value="cost">Cost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Max Units (%)</Label>
                <Input type="number" value={newResource.max_units} onChange={(e) => setNewResource(p => ({...p, max_units: parseFloat(e.target.value) || 100}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Availability (%)</Label>
                <Input type="number" value={newResource.availability_pct ?? 100} onChange={(e) => setNewResource(p => ({...p, availability_pct: parseFloat(e.target.value) || 100}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Available From</Label>
                <Input type="date" value={newResource.available_from || ''} onChange={(e) => setNewResource(p => ({...p, available_from: e.target.value}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Available To</Label>
                <Input type="date" value={newResource.available_to || ''} onChange={(e) => setNewResource(p => ({...p, available_to: e.target.value}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Calendar ID</Label>
                <Input value={newResource.calendar_id || ''} placeholder="default" onChange={(e) => setNewResource(p => ({...p, calendar_id: e.target.value}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
            </div>

            {/* ── Cost Breakdown ─────────────────────── */}
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pt-2">Cost Breakdown</div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-slate-300 text-xs">Standard Rate</Label>
                <Input type="number" value={newResource.standard_rate} onChange={(e) => setNewResource(p => ({...p, standard_rate: parseFloat(e.target.value) || 0}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Overtime Rate</Label>
                <Input type="number" value={newResource.overtime_rate || 0} onChange={(e) => setNewResource(p => ({...p, overtime_rate: parseFloat(e.target.value) || 0}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Benefits %</Label>
                <Input type="number" value={newResource.benefits_pct || 0} onChange={(e) => setNewResource(p => ({...p, benefits_pct: parseFloat(e.target.value) || 0}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Overhead %</Label>
                <Input type="number" value={newResource.overhead_pct || 0} onChange={(e) => setNewResource(p => ({...p, overhead_pct: parseFloat(e.target.value) || 0}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Currency</Label>
                <Select value={newResource.currency || 'INR'} onValueChange={(v) => setNewResource(p => ({...p, currency: v}))}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {['INR','USD','EUR','GBP','AED','SGD'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Rate Unit</Label>
                <Select value={newResource.rate_unit || 'hour'} onValueChange={(v) => setNewResource(p => ({...p, rate_unit: v}))}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {['hour','day','week','month'].map(u => <SelectItem key={u} value={u}>Per {u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Cost/Use</Label>
                <Input type="number" value={newResource.cost_per_use || 0} onChange={(e) => setNewResource(p => ({...p, cost_per_use: parseFloat(e.target.value) || 0}))} className="bg-slate-900 border-slate-600 text-white h-9" />
              </div>
              <div className="col-span-4 text-[10px] text-slate-500">
                Fully loaded ≈ {(((newResource.standard_rate || 0) * (1 + (newResource.benefits_pct || 0) / 100) * (1 + (newResource.overhead_pct || 0) / 100)) || 0).toFixed(2)} {newResource.currency || 'INR'}/{newResource.rate_unit || 'hour'}
              </div>
            </div>

            {/* ── Skills ─────────────────────────────── */}
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pt-2">Skills & Certifications</div>
            <div>
              <Label className="text-slate-300 text-xs">Skills (comma-separated)</Label>
              <Input value={(newResource.skills || []).join(', ')} placeholder="Welder, Pipe-fitter, Refractory" onChange={(e) => setNewResource(p => ({...p, skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))} className="bg-slate-900 border-slate-600 text-white h-9" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Certifications (comma-separated)</Label>
              <Input value={(newResource.certifications || []).join(', ')} placeholder="OSHA, ASME IX, ISO 45001" onChange={(e) => setNewResource(p => ({...p, certifications: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))} className="bg-slate-900 border-slate-600 text-white h-9" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Notes</Label>
              <Input value={newResource.notes || ''} onChange={(e) => setNewResource(p => ({...p, notes: e.target.value}))} className="bg-slate-900 border-slate-600 text-white h-9" placeholder="Special constraints, contract terms, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={addResource} className="bg-[#1E4E8C]" data-testid="resource-save-btn">{editingResource ? 'Save Changes' : 'Add Resource'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    <LevellingDialog
      open={showLevellingDialog}
      onOpenChange={setShowLevellingDialog}
      data={levellingSuggestions}
      onApply={applyLevelling}
      applying={applyingLevel}
    />
    </div>
  );
}

// ============================================
// LEVELLING DIALOG
// ============================================
function LevellingDialog({ open, onOpenChange, data, onApply, applying }) {
  const [selected, setSelected] = useState(new Set());

  React.useEffect(() => {
    if (data?.suggestions) {
      // Pre-select all non-critical suggestions
      setSelected(new Set(data.suggestions.filter(s => !s.is_critical).map(s => s.assignment_id)));
    } else {
      setSelected(new Set());
    }
  }, [data]);

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const suggestions = data?.suggestions || [];
  const grouped = suggestions.reduce((acc, s) => {
    (acc[s.resource_name] = acc[s.resource_name] || []).push(s);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-slate-800 dark:text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" /> Level to Capacity — Proposed Shifts
          </DialogTitle>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {suggestions.length === 0
              ? 'No overallocations found — schedule is healthy.'
              : `${suggestions.length} shift(s) suggested across ${Object.keys(grouped).length} resource(s). Critical-path tasks excluded by default.`}
          </p>
        </DialogHeader>

        {suggestions.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto space-y-4 py-2">
            {Object.entries(grouped).map(([resName, items]) => (
              <div key={resName} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <div className="bg-slate-100 dark:bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 flex justify-between items-center">
                  <span>{resName}</span>
                  <span className="text-[10px] text-slate-500">{items.length} task(s) · +{items.reduce((sum, i) => sum + (i.delay_days || 0), 0)}d total shift</span>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800/50">
                    <tr>
                      <th className="p-2 w-8"></th>
                      <th className="p-2 text-left text-slate-600 dark:text-slate-400">Task</th>
                      <th className="p-2 text-left text-slate-600 dark:text-slate-400">Current Start</th>
                      <th className="p-2 text-left text-slate-600 dark:text-slate-400">New Start</th>
                      <th className="p-2 text-right text-slate-600 dark:text-slate-400">Delay</th>
                      <th className="p-2 text-center text-slate-600 dark:text-slate-400">Critical</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(s => (
                      <tr key={s.assignment_id} className="border-t border-slate-200 dark:border-slate-700">
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={selected.has(s.assignment_id)}
                            onChange={() => toggle(s.assignment_id)}
                            className="rounded"
                            data-testid={`level-check-${s.assignment_id}`}
                          />
                        </td>
                        <td className="p-2 text-slate-800 dark:text-white">{s.task_name || s.task_id}</td>
                        <td className="p-2 text-slate-600 dark:text-slate-400">{s.current_start?.slice(0, 10)}</td>
                        <td className="p-2 text-emerald-600 dark:text-emerald-400 font-medium">{s.new_start?.slice(0, 10)}</td>
                        <td className="p-2 text-right text-amber-600 dark:text-amber-400">+{s.delay_days}d</td>
                        <td className="p-2 text-center">
                          {s.is_critical ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-300">CP</span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={applying || selected.size === 0}
            onClick={() => onApply(Array.from(selected))}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="apply-levelling-btn"
          >
            {applying ? 'Applying…' : `Apply ${selected.size} shift(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// EVM METRICS TAB COMPONENT
// ============================================
function EVMMetricsTab({ metrics, tasks }) {
  const evmData = [
    { name: 'PV', value: metrics?.bcws || 0, label: 'Planned Value (BCWS)', color: '#1E4E8C' },
    { name: 'EV', value: metrics?.bcwp || 0, label: 'Earned Value (BCWP)', color: '#0E7C6B' },
    { name: 'AC', value: metrics?.acwp || 0, label: 'Actual Cost (ACWP)', color: '#F59E0B' }
  ];
  
  const indices = [
    { name: 'SPI', value: metrics?.spi || 1, label: 'Schedule Performance Index', good: (metrics?.spi || 1) >= 1 },
    { name: 'CPI', value: metrics?.cpi || 1, label: 'Cost Performance Index', good: (metrics?.cpi || 1) >= 1 },
    { name: 'SV', value: metrics?.schedule_variance || 0, label: 'Schedule Variance', unit: '$' },
    { name: 'CV', value: metrics?.cost_variance || 0, label: 'Cost Variance', unit: '$' }
  ];
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {evmData.map(d => (
          <div key={d.name} className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">{d.label}</span>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
            </div>
            <div className="text-2xl font-bold text-white">${d.value.toLocaleString()}</div>
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-4 gap-4">
        {indices.map(i => (
          <div key={i.name} className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
            <div className="text-sm text-slate-500 mb-1">{i.label}</div>
            <div className={`text-2xl font-bold ${
              i.good !== undefined ? (i.good ? 'text-green-400' : 'text-red-400') :
              i.value >= 0 ? 'text-green-400' : 'text-red-400'
            }`}>
              {i.unit || ''}{typeof i.value === 'number' ? i.value.toFixed(2) : i.value}
            </div>
          </div>
        ))}
      </div>
      
      <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
        <h4 className="font-semibold text-white mb-4">EVM Trend</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={[
              { period: 'Week 1', pv: 10000, ev: 8000, ac: 9000 },
              { period: 'Week 2', pv: 25000, ev: 22000, ac: 24000 },
              { period: 'Week 3', pv: 45000, ev: 40000, ac: 43000 },
              { period: 'Week 4', pv: 70000, ev: 65000, ac: 68000 },
              { period: 'Week 5', pv: 100000, ev: 85000, ac: 95000 }
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="period" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <RechartsTooltip contentStyle={{ backgroundColor: '#0c1222', border: '1px solid #1e3a5f' }} />
              <Legend />
              <Line type="monotone" dataKey="pv" name="Planned Value" stroke="#1E4E8C" strokeWidth={2} />
              <Line type="monotone" dataKey="ev" name="Earned Value" stroke="#0E7C6B" strokeWidth={2} />
              <Line type="monotone" dataKey="ac" name="Actual Cost" stroke="#F59E0B" strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ============================================
// S-CURVE TAB COMPONENT
// ============================================
function SCurveTab({ data, scheduleProjectId }) {
  if (!data) return <div className="text-slate-500 p-4">Loading S-Curve data...</div>;
  
  // Calculate FTM (For This Month) and MTD (Month to Date) metrics
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  // S-Curve data from backend is already cumulative per the specification
  // Backend implements: Plan, Actual, Forecast curves following enterprise logic
  const sCurvePoints = (data.s_curve_data || []).map(point => ({
    ...point,
    // Map to display names for chart
    plan_cumulative: point.plan ?? 0,
    actual_cumulative: point.actual ?? 0,
    forecast_cumulative: point.forecast ?? 0
  }));
  
  // Compute the S-curve x-axis date closest to the data-date, for the
  // ReferenceLine on the S-Curve chart. Uses the ISO 'date' field because
  // period_label values (like 'Aug 01') repeat across years and confuse
  // recharts' categorical positioning.
  // (The chart itself now lives in ScheduleSCurveChart, which owns this logic
  //  together with the daily/weekly/monthly scale selector.)
  
  // Get current month data for FTM calculations
  const currentMonthData = sCurvePoints.filter(point => {
    try {
      const pointDate = new Date(point.date);
      return pointDate.getMonth() === currentMonth && pointDate.getFullYear() === currentYear;
    } catch {
      return false;
    }
  });
  
  // Calculate FTM Plan % (planned progress for this month)
  const ftmPlan = currentMonthData.length > 1 
    ? (currentMonthData[currentMonthData.length - 1]?.plan_cumulative || 0) - 
      (currentMonthData[0]?.plan_cumulative || 0)
    : data.summary?.plan_complete || 0;
  
  // Calculate FTM Actual % (actual progress for this month)
  const ftmActual = currentMonthData.length > 1
    ? (currentMonthData[currentMonthData.length - 1]?.actual_cumulative || 0) - 
      (currentMonthData[0]?.actual_cumulative || 0)
    : data.summary?.actual_complete || 0;
  
  // MTD Actual = Latest actual cumulative or summary
  const mtdActual = sCurvePoints.length > 0 
    ? sCurvePoints[sCurvePoints.length - 1]?.actual_cumulative || data.summary?.actual_complete || 0
    : data.summary?.actual_complete || 0;
  
  // Bar chart data for FTM comparison
  const barData = [
    { name: 'FTM Plan', value: Math.abs(ftmPlan || data.summary?.plan_complete || 0), fill: '#1E4E8C' },
    { name: 'FTM Actual', value: Math.abs(ftmActual || data.summary?.actual_complete || 0), fill: '#0E7C6B' },
    { name: 'MTD Actual', value: Math.abs(mtdActual), fill: '#F97316' }
  ];
  
  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
          <div className="text-sm text-slate-500">Plan %</div>
          <div className="text-2xl font-bold text-blue-400">{(data.summary?.plan_complete || 0).toFixed(1)}%</div>
        </div>
        <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
          <div className="text-sm text-slate-500">Actual %</div>
          <div className="text-2xl font-bold text-green-400">{(data.summary?.actual_complete || 0).toFixed(1)}%</div>
        </div>
        <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
          <div className="text-sm text-slate-500">Forecast %</div>
          <div className="text-2xl font-bold text-orange-400">{(data.summary?.forecast_complete || 0).toFixed(1)}%</div>
        </div>
        <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
          <div className="text-sm text-slate-500">Variance</div>
          <div className={`text-2xl font-bold ${(data.summary?.variance || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {(data.summary?.variance || 0) >= 0 ? '+' : ''}{(data.summary?.variance || 0).toFixed(1)}%
          </div>
        </div>
        <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
          <div className="text-sm text-slate-500">Total Weight</div>
          <div className="text-2xl font-bold text-white">{data.summary?.total_weight || 100}</div>
        </div>
      </div>
      
      {/* Status Date Info */}
      {data.summary?.status_date && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2 flex items-center gap-2">
          <span className="text-slate-400 text-sm">Data Date:</span>
          <span className="text-white font-medium">{data.summary.status_date}</span>
          <span className="text-slate-500 text-sm ml-4">({data.summary?.total_tasks || 0} activities)</span>
        </div>
      )}
      
      {/* S-Curve Chart — shared component, selectable daily/weekly/monthly scale */}
      <ScheduleSCurveChart
        url={scheduleProjectId ? `/scheduling/projects/${scheduleProjectId}/s-curve` : null}
        height={320}
        defaultInterval="week"
        testIdPrefix="sched-scurve"
      />
      
      
      {/* FTM and MTD Bar Chart */}
      <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-6">
        <h4 className="font-semibold text-white mb-4">Monthly Progress Comparison</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis type="number" stroke="#64748b" fontSize={12} domain={[0, 'auto']} tickFormatter={(v) => `${v}%`} />
              <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={100} />
              <RechartsTooltip 
                contentStyle={{ backgroundColor: '#0c1222', border: '1px solid #1e3a5f' }}
                formatter={(value) => [`${value?.toFixed(1) || 0}%`]}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {barData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4 text-center">
          <div className="p-3 bg-slate-800/50 rounded-lg">
            <div className="text-xs text-slate-500">FTM Plan %</div>
            <div className="text-lg font-semibold text-blue-400">{ftmPlan.toFixed(1)}%</div>
          </div>
          <div className="p-3 bg-slate-800/50 rounded-lg">
            <div className="text-xs text-slate-500">FTM Actual %</div>
            <div className="text-lg font-semibold text-green-400">{ftmActual.toFixed(1)}%</div>
          </div>
          <div className="p-3 bg-slate-800/50 rounded-lg">
            <div className="text-xs text-slate-500">MTD Actual %</div>
            <div className="text-lg font-semibold text-orange-400">{mtdActual.toFixed(1)}%</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// ENHANCED RISK ANALYSIS TAB COMPONENT
// ============================================
function RollUpTabContent({ projectId }) {
  const [selectedMacro, setSelectedMacro] = useState(null);
  return (
    <div className="space-y-4" data-testid="rollup-tab-content">
      <RollUpDashboard projectId={projectId} onSelectMacro={setSelectedMacro} />
      {selectedMacro && (
        <MacroDetailPanel projectId={projectId} macro={selectedMacro} onClose={() => setSelectedMacro(null)} />
      )}
    </div>
  );
}


function RiskAnalysisTabEnhanced({ projectId, tasks }) {
  const { can: canPerm } = usePermissions();
  const canSchedCreate = canPerm('MACRO_SCHEDULE', 'create');
  const [activeRiskTab, setActiveRiskTab] = useState('montecarlo');
  const [running, setRunning] = useState(false);
  const [simulationResults, setSimulationResults] = useState(null);
  
  // Risk Register state
  const [riskRegister, setRiskRegister] = useState([
    { id: '1', name: 'Weather Delays', probability: 0.6, impact: 0.8, category: 'External', owner: 'PM', status: 'Active', response: 'Mitigation' },
    { id: '2', name: 'Resource Shortage', probability: 0.4, impact: 0.7, category: 'Resource', owner: 'RM', status: 'Active', response: 'Contingency' },
    { id: '3', name: 'Permit Approval Delay', probability: 0.5, impact: 0.9, category: 'Regulatory', owner: 'PM', status: 'Monitoring', response: 'Avoidance' },
    { id: '4', name: 'Material Cost Increase', probability: 0.7, impact: 0.5, category: 'Financial', owner: 'FM', status: 'Active', response: 'Transfer' },
    { id: '5', name: 'Design Changes', probability: 0.3, impact: 0.6, category: 'Technical', owner: 'Lead', status: 'Identified', response: 'Acceptance' }
  ]);
  
  // Matrix filter state - selected cell for filtering risk list
  const [selectedMatrixCell, setSelectedMatrixCell] = useState(null);
  
  // Add Risk dialog state
  const [showAddRiskDialog, setShowAddRiskDialog] = useState(false);
  const [newRisk, setNewRisk] = useState({
    name: '',
    probability: 0.5,
    impact: 0.5,
    category: 'External',
    owner: '',
    status: 'Identified',
    response: 'Mitigation'
  });
  
  // Filter risks based on selected matrix cell
  const filteredRisks = selectedMatrixCell 
    ? riskRegister.filter(r => 
        Math.abs(r.probability - selectedMatrixCell.prob) < 0.15 && 
        Math.abs(r.impact - selectedMatrixCell.impact) < 0.15
      )
    : riskRegister;
  
  // Add new risk
  const addRisk = () => {
    if (!newRisk.name.trim()) {
      toast.error('Please enter a risk name');
      return;
    }
    const risk = {
      ...newRisk,
      id: `risk-${Date.now()}`
    };
    setRiskRegister(prev => [...prev, risk]);
    setNewRisk({
      name: '',
      probability: 0.5,
      impact: 0.5,
      category: 'External',
      owner: '',
      status: 'Identified',
      response: 'Mitigation'
    });
    setShowAddRiskDialog(false);
    toast.success('Risk added successfully');
  };
  
  // Risk Trend data (mock)
  const riskTrendData = [
    { month: 'Jan', high: 3, medium: 5, low: 8, total: 16 },
    { month: 'Feb', high: 4, medium: 6, low: 7, total: 17 },
    { month: 'Mar', high: 3, medium: 4, low: 9, total: 16 },
    { month: 'Apr', high: 2, medium: 5, low: 8, total: 15 },
    { month: 'May', high: 2, medium: 4, low: 6, total: 12 },
    { month: 'Jun', high: 1, medium: 3, low: 5, total: 9 }
  ];

  const runSimulation = async () => {
    setRunning(true);
    try {
      const res = await api.post(`/scheduling/projects/${projectId}/monte-carlo/schedule`, {
        iterations: 1000,
        distribution: 'triangular'
      });
      setSimulationResults(res.data);
    } catch (err) {
      toast.error('Simulation failed');
    } finally {
      setRunning(false);
    }
  };

  const getRiskScore = (prob, impact) => prob * impact;
  const getRiskLevel = (score) => {
    if (score >= 0.5) return { label: 'High', color: 'text-red-400', bg: 'bg-red-500/20' };
    if (score >= 0.25) return { label: 'Medium', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    return { label: 'Low', color: 'text-green-400', bg: 'bg-green-500/20' };
  };

  return (
    <div className="space-y-4">
      {/* Risk Sub-tabs */}
      <Tabs value={activeRiskTab} onValueChange={setActiveRiskTab}>
        <TabsList className="bg-[#0c1222] border border-slate-700/50 p-0.5">
          <TabsTrigger value="montecarlo" className="data-[state=active]:bg-slate-800 px-4 py-1.5 rounded">Monte Carlo</TabsTrigger>
          <TabsTrigger value="register" className="data-[state=active]:bg-slate-800 px-4 py-1.5 rounded">Risk Register</TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-slate-800 px-4 py-1.5 rounded">Risk Trends</TabsTrigger>
        </TabsList>

        {/* Monte Carlo Simulation */}
        <TabsContent value="montecarlo" className="mt-4">
          <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Monte Carlo Simulation</h3>
                <p className="text-sm text-slate-500">Run probabilistic schedule analysis</p>
              </div>
              <Button onClick={runSimulation} disabled={running} className="bg-[#1E4E8C]">
                {running ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />}
                Run Simulation
              </Button>
            </div>
            
            {simulationResults ? (
              <div className="space-y-4">
                {/* Statistics Cards */}
                <div className="grid grid-cols-5 gap-4">
                  <div className="bg-[#0a0f1e] rounded-lg p-3">
                    <div className="text-xs text-slate-500">P50 (Median)</div>
                    <div className="text-lg font-bold text-white">
                      {simulationResults.completion_dates?.p50 ? new Date(simulationResults.completion_dates.p50).toLocaleDateString() : '-'}
                    </div>
                  </div>
                  <div className="bg-[#0a0f1e] rounded-lg p-3">
                    <div className="text-xs text-slate-500">P80</div>
                    <div className="text-lg font-bold text-amber-400">
                      {simulationResults.completion_dates?.p80 ? new Date(simulationResults.completion_dates.p80).toLocaleDateString() : '-'}
                    </div>
                  </div>
                  <div className="bg-[#0a0f1e] rounded-lg p-3">
                    <div className="text-xs text-slate-500">P90</div>
                    <div className="text-lg font-bold text-orange-400">
                      {simulationResults.completion_dates?.p90 ? new Date(simulationResults.completion_dates.p90).toLocaleDateString() : '-'}
                    </div>
                  </div>
                  <div className="bg-[#0a0f1e] rounded-lg p-3">
                    <div className="text-xs text-slate-500">P95</div>
                    <div className="text-lg font-bold text-red-400">
                      {simulationResults.completion_dates?.p95 ? new Date(simulationResults.completion_dates.p95).toLocaleDateString() : '-'}
                    </div>
                  </div>
                  <div className="bg-[#0a0f1e] rounded-lg p-3">
                    <div className="text-xs text-slate-500">Iterations</div>
                    <div className="text-lg font-bold text-white">{simulationResults.iterations || 1000}</div>
                  </div>
                </div>

                {/* Statistics Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#0a0f1e] rounded-lg p-3">
                    <div className="text-xs text-slate-500">Mean Duration</div>
                    <div className="text-lg font-bold text-white">{simulationResults.statistics?.mean?.toFixed(1) || 0}h</div>
                  </div>
                  <div className="bg-[#0a0f1e] rounded-lg p-3">
                    <div className="text-xs text-slate-500">Std Deviation</div>
                    <div className="text-lg font-bold text-white">{simulationResults.statistics?.std_dev?.toFixed(1) || 0}h</div>
                  </div>
                  <div className="bg-[#0a0f1e] rounded-lg p-3">
                    <div className="text-xs text-slate-500">Range</div>
                    <div className="text-lg font-bold text-white">{simulationResults.statistics?.min?.toFixed(0) || 0} - {simulationResults.statistics?.max?.toFixed(0) || 0}h</div>
                  </div>
                </div>
                
                {/* Histogram */}
                <div className="bg-[#0a0f1e] rounded-lg p-4">
                  <h5 className="text-sm font-medium text-white mb-2">Distribution Histogram</h5>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={simulationResults.histogram || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                        <XAxis dataKey="bin_start" stroke="#64748b" fontSize={10} tickFormatter={(v) => `${Math.round(v)}h`} />
                        <YAxis stroke="#64748b" fontSize={12} />
                        <RechartsTooltip 
                          contentStyle={{ backgroundColor: '#0c1222', border: '1px solid #1e3a5f' }} 
                          formatter={(value, name) => [`${value} occurrences`, 'Count']}
                        />
                        <Bar dataKey="count" fill="#1E4E8C" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-600">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Run simulation to see probabilistic results</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Risk Register */}
        <TabsContent value="register" className="mt-4">
          <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-700/30">
              <div>
                <h3 className="text-lg font-semibold text-white">Risk Register</h3>
                <p className="text-sm text-slate-500">Track and manage project risks</p>
              </div>
              {canSchedCreate && <Button 
                className="bg-[#1E4E8C]" 
                onClick={() => setShowAddRiskDialog(true)}
                data-testid="add-risk-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Risk
              </Button>}
            </div>

            {/* Probability/Impact Matrix */}
            <div className="p-4 border-b border-slate-700/30">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-white">Probability / Impact Matrix</h4>
                {selectedMatrixCell && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedMatrixCell(null)}
                    className="text-xs text-slate-400 hover:text-white"
                  >
                    Clear Filter
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-6 gap-1 max-w-lg">
                {/* Y-axis label */}
                <div className="text-xs text-slate-500 flex items-center">Prob</div>
                <div className="text-xs text-slate-500 text-center">Very Low</div>
                <div className="text-xs text-slate-500 text-center">Low</div>
                <div className="text-xs text-slate-500 text-center">Medium</div>
                <div className="text-xs text-slate-500 text-center">High</div>
                <div className="text-xs text-slate-500 text-center">Very High</div>
                
                {/* Matrix rows */}
                {[0.9, 0.7, 0.5, 0.3, 0.1].map((prob, pIdx) => (
                  <React.Fragment key={`row-${prob}`}>
                    <div className="text-xs text-slate-500 flex items-center justify-end pr-2">{prob}</div>
                    {[0.1, 0.3, 0.5, 0.7, 0.9].map((imp, iIdx) => {
                      const score = prob * imp;
                      const risksInCell = riskRegister.filter(r => 
                        Math.abs(r.probability - prob) < 0.15 && Math.abs(r.impact - imp) < 0.15
                      );
                      const bg = score >= 0.5 ? 'bg-red-500/30' : score >= 0.25 ? 'bg-amber-500/30' : 'bg-green-500/30';
                      const isSelected = selectedMatrixCell && selectedMatrixCell.prob === prob && selectedMatrixCell.impact === imp;
                      return (
                        <div 
                          key={`${prob}-${imp}`} 
                          className={`h-10 ${bg} rounded flex items-center justify-center cursor-pointer transition-all hover:ring-2 hover:ring-white/50 ${isSelected ? 'ring-2 ring-[#1E4E8C]' : ''}`}
                          onClick={() => setSelectedMatrixCell(isSelected ? null : { prob, impact: imp })}
                          data-testid={`matrix-cell-${prob}-${imp}`}
                        >
                          {risksInCell.length > 0 && (
                            <span className="text-xs font-bold text-white">{risksInCell.length}</span>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                <div className="text-xs text-slate-500"></div>
                <div className="text-xs text-slate-500 text-center pt-1" style={{ gridColumn: 'span 5' }}>Impact</div>
              </div>
            </div>
            
            {/* Risk List */}
            <div className="overflow-auto">
              {selectedMatrixCell && (
                <div className="px-4 py-2 bg-[#1E4E8C]/10 border-b border-slate-700/30">
                  <span className="text-sm text-slate-400">
                    Showing {filteredRisks.length} risk(s) with Probability ~{(selectedMatrixCell.prob * 100).toFixed(0)}% and Impact ~{(selectedMatrixCell.impact * 100).toFixed(0)}%
                  </span>
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="bg-[#0a0f1e]">
                  <tr>
                    <th className="p-3 text-left text-xs text-slate-500">Risk</th>
                    <th className="p-3 text-center text-xs text-slate-500">Probability</th>
                    <th className="p-3 text-center text-xs text-slate-500">Impact</th>
                    <th className="p-3 text-center text-xs text-slate-500">Score</th>
                    <th className="p-3 text-center text-xs text-slate-500">Level</th>
                    <th className="p-3 text-left text-xs text-slate-500">Category</th>
                    <th className="p-3 text-left text-xs text-slate-500">Response</th>
                    <th className="p-3 text-center text-xs text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRisks.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-500">
                        {selectedMatrixCell ? 'No risks match the selected filter' : 'No risks registered yet'}
                      </td>
                    </tr>
                  ) : (
                    filteredRisks.map(risk => {
                      const score = getRiskScore(risk.probability, risk.impact);
                      const level = getRiskLevel(score);
                      return (
                        <tr key={risk.id} className="border-b border-slate-700/30 hover:bg-slate-800/30">
                          <td className="p-3 text-white">{risk.name}</td>
                          <td className="p-3 text-center text-slate-300">{(risk.probability * 100).toFixed(0)}%</td>
                          <td className="p-3 text-center text-slate-300">{(risk.impact * 100).toFixed(0)}%</td>
                          <td className="p-3 text-center font-bold text-white">{score.toFixed(2)}</td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${level.bg} ${level.color}`}>
                              {level.label}
                            </span>
                          </td>
                          <td className="p-3 text-slate-400">{risk.category}</td>
                          <td className="p-3 text-slate-400">{risk.response}</td>
                          <td className="p-3 text-center">
                            <Badge variant="outline" className="border-slate-600 text-xs">{risk.status}</Badge>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Add Risk Dialog */}
          <Dialog open={showAddRiskDialog} onOpenChange={setShowAddRiskDialog}>
            <DialogContent className="bg-slate-800 border-slate-700">
              <DialogHeader>
                <DialogTitle className="text-white">Add New Risk</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-slate-300">Risk Name</Label>
                  <Input
                    value={newRisk.name}
                    onChange={(e) => setNewRisk({ ...newRisk, name: e.target.value })}
                    placeholder="e.g., Material Shortage"
                    className="bg-slate-900 border-slate-600 text-white"
                    data-testid="risk-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Probability ({(newRisk.probability * 100).toFixed(0)}%)</Label>
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={newRisk.probability * 100}
                      onChange={(e) => setNewRisk({ ...newRisk, probability: parseInt(e.target.value) / 100 })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Impact ({(newRisk.impact * 100).toFixed(0)}%)</Label>
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={newRisk.impact * 100}
                      onChange={(e) => setNewRisk({ ...newRisk, impact: parseInt(e.target.value) / 100 })}
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Category</Label>
                    <Select value={newRisk.category} onValueChange={(val) => setNewRisk({ ...newRisk, category: val })}>
                      <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="External">External</SelectItem>
                        <SelectItem value="Technical">Technical</SelectItem>
                        <SelectItem value="Resource">Resource</SelectItem>
                        <SelectItem value="Financial">Financial</SelectItem>
                        <SelectItem value="Regulatory">Regulatory</SelectItem>
                        <SelectItem value="Schedule">Schedule</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Response Strategy</Label>
                    <Select value={newRisk.response} onValueChange={(val) => setNewRisk({ ...newRisk, response: val })}>
                      <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="Mitigation">Mitigation</SelectItem>
                        <SelectItem value="Avoidance">Avoidance</SelectItem>
                        <SelectItem value="Transfer">Transfer</SelectItem>
                        <SelectItem value="Acceptance">Acceptance</SelectItem>
                        <SelectItem value="Contingency">Contingency</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Owner</Label>
                  <Input
                    value={newRisk.owner}
                    onChange={(e) => setNewRisk({ ...newRisk, owner: e.target.value })}
                    placeholder="e.g., Project Manager"
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddRiskDialog(false)}>Cancel</Button>
                <Button onClick={addRisk} className="bg-[#1E4E8C]" data-testid="save-risk-btn">Add Risk</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Risk Trends */}
        <TabsContent value="trends" className="mt-4">
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
                <div className="text-sm text-slate-500">Total Active Risks</div>
                <div className="text-2xl font-bold text-white">{riskRegister.length}</div>
              </div>
              <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
                <div className="text-sm text-slate-500">High Priority</div>
                <div className="text-2xl font-bold text-red-400">
                  {riskRegister.filter(r => getRiskScore(r.probability, r.impact) >= 0.5).length}
                </div>
              </div>
              <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
                <div className="text-sm text-slate-500">Medium Priority</div>
                <div className="text-2xl font-bold text-amber-400">
                  {riskRegister.filter(r => {
                    const s = getRiskScore(r.probability, r.impact);
                    return s >= 0.25 && s < 0.5;
                  }).length}
                </div>
              </div>
              <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
                <div className="text-sm text-slate-500">Low Priority</div>
                <div className="text-2xl font-bold text-green-400">
                  {riskRegister.filter(r => getRiskScore(r.probability, r.impact) < 0.25).length}
                </div>
              </div>
            </div>

            {/* Risk Trend Chart */}
            <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-4">Risk Trend Over Time</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={riskTrendData}>
                    <defs>
                      <linearGradient id="highGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="medGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="lowGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <RechartsTooltip contentStyle={{ backgroundColor: '#0c1222', border: '1px solid #1e3a5f' }} />
                    <Legend />
                    <Area type="monotone" dataKey="high" name="High" stroke="#ef4444" fill="url(#highGrad)" stackId="1" />
                    <Area type="monotone" dataKey="medium" name="Medium" stroke="#f59e0b" fill="url(#medGrad)" stackId="1" />
                    <Area type="monotone" dataKey="low" name="Low" stroke="#22c55e" fill="url(#lowGrad)" stackId="1" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Risk Burndown */}
            <div className="bg-[#0c1222] border border-slate-700/50 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-4">Risk Burndown</h4>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsLineChart data={riskTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <RechartsTooltip contentStyle={{ backgroundColor: '#0c1222', border: '1px solid #1e3a5f' }} />
                    <Line type="monotone" dataKey="total" name="Total Risks" stroke="#1E4E8C" strokeWidth={2} dot={{ fill: '#1E4E8C' }} />
                  </RechartsLineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
